import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Pressable,
  TextInput,
  Modal,
  Image,
  Dimensions,
  PanResponder,
  Animated,
  Platform,
  useColorScheme,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { View, Text } from '../components/Themed';
import { useQuery, gql } from '@apollo/client';
import { MaterialIcons, MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Config from '../constants/Config';
import { getProductImageUrl } from '../utils/urls';
import i18n from '../utils/localization';
import AllergenLegend from '../components/AllergenLegend';
import AllergenBadges from '../components/AllergenBadges';

// Product image component - uses native img tag on web to bypass CORS restrictions
const ProductImage = ({ uri, style, resizeMode = 'contain' }) => {
  if (!uri) return null;

  // On web, use native img tag which doesn't have CORS restrictions for display
  if (Platform.OS === 'web') {
    const { width, height, borderRadius, marginBottom, marginRight } = style || {};

    return (
      <img
        src={uri}
        alt=""
        data-product-image={uri}
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
          borderRadius: typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius,
          marginBottom: typeof marginBottom === 'number' ? `${marginBottom}px` : marginBottom,
          marginRight: typeof marginRight === 'number' ? `${marginRight}px` : marginRight,
          objectFit: resizeMode === 'cover' ? 'cover' : 'contain',
        }}
        onError={(e) => console.error('[ProductImage] Load error:', uri)}
      />
    );
  }

  // On native, use standard Image component
  return (
    <Image
      source={{ uri }}
      style={style}
      resizeMode={resizeMode}
    />
  );
};

const PRODUCTS_QUERY = gql`
  query getProducts($token: String!) {
    categories(token: $token) {
      id
      name
      order
    }
    products(token: $token) {
      id
      category
      title
      subtitle
      imageUrls
      prices
      variants
      volumes
      allergens
    }
  }
`;

// Page size presets (dimensions in mm, with screen-scaled pixel values)
// At 72 DPI: 1mm = 2.834645669 pixels
const PAGE_SIZES = {
  A3: {
    name: 'A3',
    width: 297,
    height: 420,
    // Screen display sizes (scaled to fit typical screens)
    screenWidth: 420,
    screenHeight: 594,
    description: '297 x 420 mm',
    icon: 'crop-portrait',
  },
  A3_LANDSCAPE: {
    name: 'A3 Landscape',
    width: 420,
    height: 297,
    screenWidth: 594,
    screenHeight: 420,
    description: '420 x 297 mm',
    icon: 'crop-landscape',
  },
  A4: {
    name: 'A4',
    width: 210,
    height: 297,
    screenWidth: 350,
    screenHeight: 495,
    description: '210 x 297 mm',
    icon: 'crop-portrait',
  },
  A4_LANDSCAPE: {
    name: 'A4 Landscape',
    width: 297,
    height: 210,
    screenWidth: 495,
    screenHeight: 350,
    description: '297 x 210 mm',
    icon: 'crop-landscape',
  },
  A5: {
    name: 'A5',
    width: 148,
    height: 210,
    screenWidth: 280,
    screenHeight: 396,
    description: '148 x 210 mm',
    icon: 'crop-portrait',
  },
  A5_LANDSCAPE: {
    name: 'A5 Landscape',
    width: 210,
    height: 148,
    screenWidth: 396,
    screenHeight: 280,
    description: '210 x 148 mm',
    icon: 'crop-landscape',
  },
  FULL_HD: {
    name: 'Full HD',
    width: 1920,
    height: 1080,
    screenWidth: 640,
    screenHeight: 360,
    description: '1920 x 1080 px',
    icon: 'tv',
  },
  FULL_HD_PORTRAIT: {
    name: 'Full HD Portrait',
    width: 1080,
    height: 1920,
    screenWidth: 360,
    screenHeight: 640,
    description: '1080 x 1920 px',
    icon: 'stay-current-portrait',
  },
  UHD_4K: {
    name: '4K UHD',
    width: 3840,
    height: 2160,
    screenWidth: 640,
    screenHeight: 360,
    description: '3840 x 2160 px',
    icon: 'tv',
  },
  UHD_4K_PORTRAIT: {
    name: '4K UHD Portrait',
    width: 2160,
    height: 3840,
    screenWidth: 360,
    screenHeight: 640,
    description: '2160 x 3840 px',
    icon: 'stay-current-portrait',
  },
};

// AI Background presets with patterns and textures
const AI_BACKGROUNDS = [
  // Solid & Gradient Backgrounds
  {
    id: 'modern-white',
    name: 'Modern White',
    category: 'solid',
    baseColor: '#FFFFFF',
    textColor: '#000000',
    pattern: null,
  },
  {
    id: 'cream-paper',
    name: 'Cream Paper',
    category: 'solid',
    baseColor: '#FFF8E7',
    textColor: '#4A3728',
    pattern: null,
  },
  {
    id: 'dark-elegant',
    name: 'Dark Elegant',
    category: 'solid',
    baseColor: '#1a1a2e',
    textColor: '#FFFFFF',
    pattern: null,
  },
  // Textured Backgrounds
  {
    id: 'wood-texture',
    name: 'Wood Grain',
    category: 'texture',
    baseColor: '#8B4513',
    secondaryColor: '#A0522D',
    textColor: '#FFFFFF',
    pattern: 'wood',
  },
  {
    id: 'marble-white',
    name: 'White Marble',
    category: 'texture',
    baseColor: '#F5F5F5',
    secondaryColor: '#E0E0E0',
    accentColor: '#CCCCCC',
    textColor: '#333333',
    pattern: 'marble',
  },
  {
    id: 'marble-dark',
    name: 'Dark Marble',
    category: 'texture',
    baseColor: '#2C3E50',
    secondaryColor: '#34495E',
    accentColor: '#1A252F',
    textColor: '#FFFFFF',
    pattern: 'marble',
  },
  {
    id: 'chalkboard',
    name: 'Chalkboard',
    category: 'texture',
    baseColor: '#2F4F4F',
    secondaryColor: '#3D5C5C',
    textColor: '#FFFFFF',
    pattern: 'chalk',
  },
  {
    id: 'kraft-paper',
    name: 'Kraft Paper',
    category: 'texture',
    baseColor: '#C4A77D',
    secondaryColor: '#B8956E',
    textColor: '#3D2914',
    pattern: 'paper',
  },
  // Patterned Backgrounds
  {
    id: 'dots-light',
    name: 'Polka Dots Light',
    category: 'pattern',
    baseColor: '#FFFFFF',
    patternColor: '#E8E8E8',
    textColor: '#333333',
    pattern: 'dots',
  },
  {
    id: 'dots-dark',
    name: 'Polka Dots Dark',
    category: 'pattern',
    baseColor: '#1E1E1E',
    patternColor: '#2D2D2D',
    textColor: '#FFFFFF',
    pattern: 'dots',
  },
  {
    id: 'stripes-classic',
    name: 'Classic Stripes',
    category: 'pattern',
    baseColor: '#FFFFFF',
    patternColor: '#F0E6D3',
    textColor: '#333333',
    pattern: 'stripes',
  },
  {
    id: 'diagonal-lines',
    name: 'Diagonal Lines',
    category: 'pattern',
    baseColor: '#F5F5DC',
    patternColor: '#E8E4C9',
    textColor: '#4A4A4A',
    pattern: 'diagonal',
  },
  {
    id: 'grid-subtle',
    name: 'Subtle Grid',
    category: 'pattern',
    baseColor: '#FAFAFA',
    patternColor: '#EEEEEE',
    textColor: '#333333',
    pattern: 'grid',
  },
  // Gradient Backgrounds
  {
    id: 'sunset-warm',
    name: 'Warm Sunset',
    category: 'gradient',
    gradientColors: ['#FF6B6B', '#FFA07A', '#FFD93D'],
    textColor: '#FFFFFF',
    pattern: 'gradient-vertical',
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    category: 'gradient',
    gradientColors: ['#1A5276', '#2980B9', '#5DADE2'],
    textColor: '#FFFFFF',
    pattern: 'gradient-vertical',
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    category: 'gradient',
    gradientColors: ['#1D4E3E', '#2E7D5A', '#52BE80'],
    textColor: '#FFFFFF',
    pattern: 'gradient-vertical',
  },
  {
    id: 'purple-haze',
    name: 'Purple Haze',
    category: 'gradient',
    gradientColors: ['#4A235A', '#7D3C98', '#AF7AC5'],
    textColor: '#FFFFFF',
    pattern: 'gradient-vertical',
  },
  {
    id: 'golden-hour',
    name: 'Golden Hour',
    category: 'gradient',
    gradientColors: ['#B7950B', '#D4AC0D', '#F4D03F'],
    textColor: '#1A1A1A',
    pattern: 'gradient-vertical',
  },
  // Vintage & Artistic
  {
    id: 'vintage-sepia',
    name: 'Vintage Sepia',
    category: 'artistic',
    baseColor: '#D2B48C',
    secondaryColor: '#C4A77D',
    borderColor: '#8B7355',
    textColor: '#3D2914',
    pattern: 'vintage',
  },
  {
    id: 'art-deco',
    name: 'Art Deco Gold',
    category: 'artistic',
    baseColor: '#1A1A2E',
    patternColor: '#D4AF37',
    textColor: '#D4AF37',
    pattern: 'artdeco',
  },
  {
    id: 'rustic-barn',
    name: 'Rustic Barn',
    category: 'artistic',
    baseColor: '#654321',
    secondaryColor: '#8B5A2B',
    textColor: '#F5F5DC',
    pattern: 'rustic',
  },
  {
    id: 'minimalist-grey',
    name: 'Minimalist Grey',
    category: 'artistic',
    baseColor: '#F5F5F5',
    borderColor: '#CCCCCC',
    textColor: '#333333',
    pattern: 'minimal',
  },
  // Additional Solid Backgrounds
  {
    id: 'midnight-black',
    name: 'Midnight Black',
    category: 'solid',
    baseColor: '#0D0D0D',
    textColor: '#FFFFFF',
    pattern: null,
  },
  {
    id: 'soft-pink',
    name: 'Soft Pink',
    category: 'solid',
    baseColor: '#FFE4E1',
    textColor: '#8B4557',
    pattern: null,
  },
  {
    id: 'mint-green',
    name: 'Mint Green',
    category: 'solid',
    baseColor: '#E8F5E9',
    textColor: '#2E7D32',
    pattern: null,
  },
  {
    id: 'lavender',
    name: 'Lavender',
    category: 'solid',
    baseColor: '#E6E6FA',
    textColor: '#4B0082',
    pattern: null,
  },
  {
    id: 'peach',
    name: 'Peach',
    category: 'solid',
    baseColor: '#FFDAB9',
    textColor: '#8B4513',
    pattern: null,
  },
  {
    id: 'slate-blue',
    name: 'Slate Blue',
    category: 'solid',
    baseColor: '#708090',
    textColor: '#FFFFFF',
    pattern: null,
  },
  // Additional Textures
  {
    id: 'linen',
    name: 'Linen',
    category: 'texture',
    baseColor: '#FAF0E6',
    secondaryColor: '#EEE5DB',
    textColor: '#4A4A4A',
    pattern: 'linen',
  },
  {
    id: 'concrete',
    name: 'Concrete',
    category: 'texture',
    baseColor: '#A9A9A9',
    secondaryColor: '#909090',
    textColor: '#FFFFFF',
    pattern: 'concrete',
  },
  {
    id: 'leather-brown',
    name: 'Brown Leather',
    category: 'texture',
    baseColor: '#8B4513',
    secondaryColor: '#A0522D',
    textColor: '#FFE4C4',
    pattern: 'leather',
  },
  {
    id: 'canvas',
    name: 'Canvas',
    category: 'texture',
    baseColor: '#F5F5DC',
    secondaryColor: '#E8E4D0',
    textColor: '#333333',
    pattern: 'canvas',
  },
  // Additional Patterns
  {
    id: 'chevron',
    name: 'Chevron',
    category: 'pattern',
    baseColor: '#FFFFFF',
    patternColor: '#E0E0E0',
    textColor: '#333333',
    pattern: 'chevron',
  },
  {
    id: 'hexagon',
    name: 'Hexagon',
    category: 'pattern',
    baseColor: '#F8F8F8',
    patternColor: '#DDDDDD',
    textColor: '#333333',
    pattern: 'hexagon',
  },
  {
    id: 'waves',
    name: 'Waves',
    category: 'pattern',
    baseColor: '#E3F2FD',
    patternColor: '#BBDEFB',
    textColor: '#1565C0',
    pattern: 'waves',
  },
  {
    id: 'diamonds',
    name: 'Diamonds',
    category: 'pattern',
    baseColor: '#FFF8E1',
    patternColor: '#FFE082',
    textColor: '#F57F17',
    pattern: 'diamonds',
  },
  {
    id: 'crosses',
    name: 'Crosses',
    category: 'pattern',
    baseColor: '#FFFFFF',
    patternColor: '#EEEEEE',
    textColor: '#333333',
    pattern: 'crosses',
  },
  // Additional Gradients
  {
    id: 'rose-gold',
    name: 'Rose Gold',
    category: 'gradient',
    gradientColors: ['#B76E79', '#E8B4B8', '#F4D4D4'],
    textColor: '#4A2C2A',
    pattern: 'gradient-vertical',
  },
  {
    id: 'midnight-sky',
    name: 'Midnight Sky',
    category: 'gradient',
    gradientColors: ['#0F0C29', '#302B63', '#24243E'],
    textColor: '#FFFFFF',
    pattern: 'gradient-vertical',
  },
  {
    id: 'autumn-leaves',
    name: 'Autumn Leaves',
    category: 'gradient',
    gradientColors: ['#8B4513', '#CD853F', '#DEB887'],
    textColor: '#FFFFFF',
    pattern: 'gradient-vertical',
  },
  {
    id: 'tropical',
    name: 'Tropical',
    category: 'gradient',
    gradientColors: ['#00BFA5', '#1DE9B6', '#A7FFEB'],
    textColor: '#004D40',
    pattern: 'gradient-vertical',
  },
  {
    id: 'berry',
    name: 'Berry',
    category: 'gradient',
    gradientColors: ['#880E4F', '#C2185B', '#F48FB1'],
    textColor: '#FFFFFF',
    pattern: 'gradient-vertical',
  },
  {
    id: 'steel',
    name: 'Steel',
    category: 'gradient',
    gradientColors: ['#37474F', '#607D8B', '#90A4AE'],
    textColor: '#FFFFFF',
    pattern: 'gradient-vertical',
  },
  // Additional Artistic
  {
    id: 'watercolor-blue',
    name: 'Watercolor Blue',
    category: 'artistic',
    baseColor: '#E3F2FD',
    secondaryColor: '#90CAF9',
    accentColor: '#42A5F5',
    textColor: '#1565C0',
    pattern: 'watercolor',
  },
  {
    id: 'brushed-metal',
    name: 'Brushed Metal',
    category: 'artistic',
    baseColor: '#C0C0C0',
    secondaryColor: '#A9A9A9',
    textColor: '#333333',
    pattern: 'brushed',
  },
  {
    id: 'torn-paper',
    name: 'Torn Paper',
    category: 'artistic',
    baseColor: '#FFFEF0',
    secondaryColor: '#F5F5DC',
    borderColor: '#D4C9A8',
    textColor: '#5D4E37',
    pattern: 'torn',
  },
  {
    id: 'neon-dark',
    name: 'Neon Dark',
    category: 'artistic',
    baseColor: '#121212',
    patternColor: '#00FF00',
    textColor: '#00FF00',
    pattern: 'neon',
  },
  {
    id: 'newspaper',
    name: 'Newspaper',
    category: 'artistic',
    baseColor: '#F5F5DC',
    secondaryColor: '#E8E4D0',
    textColor: '#333333',
    pattern: 'newspaper',
  },
];

// Background Pattern Component
const BackgroundPatternRenderer = ({ background, width, height }) => {
  if (!background) return null;

  const renderPattern = () => {
    switch (background.pattern) {
      case 'dots':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {[...Array(Math.ceil(height / 20))].map((_, rowIndex) => (
              <View key={rowIndex} style={{ flexDirection: 'row', position: 'absolute', top: rowIndex * 20 }}>
                {[...Array(Math.ceil(width / 20))].map((_, colIndex) => (
                  <View
                    key={colIndex}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: background.patternColor,
                      marginLeft: colIndex === 0 ? 7 : 14,
                      marginTop: 7,
                    }}
                  />
                ))}
              </View>
            ))}
          </View>
        );

      case 'stripes':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {[...Array(Math.ceil(width / 30))].map((_, index) => (
              <View
                key={index}
                style={{
                  position: 'absolute',
                  left: index * 30,
                  top: 0,
                  width: 15,
                  height: height,
                  backgroundColor: background.patternColor,
                }}
              />
            ))}
          </View>
        );

      case 'diagonal':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {[...Array(Math.ceil((width + height) / 25))].map((_, index) => (
              <View
                key={index}
                style={{
                  position: 'absolute',
                  left: -height + index * 25,
                  top: 0,
                  width: 12,
                  height: height * 2,
                  backgroundColor: background.patternColor,
                  transform: [{ rotate: '45deg' }],
                }}
              />
            ))}
          </View>
        );

      case 'grid':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {/* Horizontal lines */}
            {[...Array(Math.ceil(height / 30))].map((_, index) => (
              <View
                key={`h-${index}`}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: index * 30,
                  width: width,
                  height: 1,
                  backgroundColor: background.patternColor,
                }}
              />
            ))}
            {/* Vertical lines */}
            {[...Array(Math.ceil(width / 30))].map((_, index) => (
              <View
                key={`v-${index}`}
                style={{
                  position: 'absolute',
                  left: index * 30,
                  top: 0,
                  width: 1,
                  height: height,
                  backgroundColor: background.patternColor,
                }}
              />
            ))}
          </View>
        );

      case 'wood':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {[...Array(Math.ceil(height / 8))].map((_, index) => (
              <View
                key={index}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: index * 8 + (index % 3) * 2,
                  width: width,
                  height: index % 2 === 0 ? 3 : 2,
                  backgroundColor: background.secondaryColor,
                  opacity: 0.3 + (index % 5) * 0.1,
                }}
              />
            ))}
          </View>
        );

      case 'marble':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {/* Marble veins - diagonal streaks */}
            {[...Array(12)].map((_, index) => (
              <View
                key={index}
                style={{
                  position: 'absolute',
                  left: (index * width / 6) - 50,
                  top: (index % 3) * 80,
                  width: 2 + (index % 3),
                  height: height * 0.8,
                  backgroundColor: background.secondaryColor,
                  opacity: 0.2 + (index % 4) * 0.1,
                  transform: [{ rotate: `${20 + (index % 5) * 10}deg` }],
                }}
              />
            ))}
            {/* Additional accent veins */}
            {[...Array(6)].map((_, index) => (
              <View
                key={`accent-${index}`}
                style={{
                  position: 'absolute',
                  left: (index * width / 4),
                  top: 50 + (index % 2) * 100,
                  width: 1,
                  height: height * 0.5,
                  backgroundColor: background.accentColor || background.secondaryColor,
                  opacity: 0.15,
                  transform: [{ rotate: `${-15 + (index % 3) * 15}deg` }],
                }}
              />
            ))}
          </View>
        );

      case 'chalk':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {/* Chalk dust effect */}
            {[...Array(50)].map((_, index) => (
              <View
                key={index}
                style={{
                  position: 'absolute',
                  left: (index * 37) % width,
                  top: (index * 23) % height,
                  width: 2 + (index % 4),
                  height: 2 + (index % 3),
                  borderRadius: 2,
                  backgroundColor: '#FFFFFF',
                  opacity: 0.05 + (index % 10) * 0.01,
                }}
              />
            ))}
          </View>
        );

      case 'paper':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {/* Paper fiber texture */}
            {[...Array(100)].map((_, index) => (
              <View
                key={index}
                style={{
                  position: 'absolute',
                  left: (index * 17) % width,
                  top: (index * 13) % height,
                  width: 8 + (index % 12),
                  height: 1,
                  backgroundColor: background.secondaryColor,
                  opacity: 0.15 + (index % 8) * 0.02,
                  transform: [{ rotate: `${(index % 180)}deg` }],
                }}
              />
            ))}
          </View>
        );

      case 'gradient-vertical':
        const gradientColors = background.gradientColors || [background.baseColor, background.secondaryColor];
        const segmentHeight = height / (gradientColors.length - 1);
        return (
          <View style={StyleSheet.absoluteFill}>
            {gradientColors.slice(0, -1).map((color, index) => (
              <View
                key={index}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: index * segmentHeight,
                  width: width,
                  height: segmentHeight + 1,
                  backgroundColor: color,
                }}
              />
            ))}
            {/* Blend layers */}
            {gradientColors.slice(0, -1).map((color, index) => (
              <View
                key={`blend-${index}`}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: (index + 0.5) * segmentHeight,
                  width: width,
                  height: segmentHeight,
                  backgroundColor: gradientColors[index + 1],
                  opacity: 0.5,
                }}
              />
            ))}
          </View>
        );

      case 'vintage':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {/* Vintage border */}
            <View
              style={{
                position: 'absolute',
                left: 15,
                top: 15,
                right: 15,
                bottom: 15,
                borderWidth: 2,
                borderColor: background.borderColor,
                opacity: 0.5,
              }}
            />
            <View
              style={{
                position: 'absolute',
                left: 20,
                top: 20,
                right: 20,
                bottom: 20,
                borderWidth: 1,
                borderColor: background.borderColor,
                opacity: 0.3,
              }}
            />
            {/* Corner ornaments */}
            {[[15, 15], [15, null], [null, 15], [null, null]].map((pos, index) => (
              <View
                key={index}
                style={{
                  position: 'absolute',
                  left: pos[0] || undefined,
                  right: pos[0] === null ? 15 : undefined,
                  top: pos[1] || undefined,
                  bottom: pos[1] === null ? 15 : undefined,
                  width: 20,
                  height: 20,
                  borderColor: background.borderColor,
                  borderWidth: 1,
                  opacity: 0.4,
                }}
              />
            ))}
          </View>
        );

      case 'artdeco':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {/* Art deco geometric patterns */}
            <View style={{ position: 'absolute', left: 10, top: 10, right: 10, bottom: 10, borderWidth: 1, borderColor: background.patternColor, opacity: 0.6 }} />
            <View style={{ position: 'absolute', left: 20, top: 20, right: 20, bottom: 20, borderWidth: 1, borderColor: background.patternColor, opacity: 0.4 }} />
            {/* Corner triangles */}
            {[0, 1, 2, 3].map((corner) => (
              <View
                key={corner}
                style={{
                  position: 'absolute',
                  left: corner < 2 ? 10 : undefined,
                  right: corner >= 2 ? 10 : undefined,
                  top: corner % 2 === 0 ? 10 : undefined,
                  bottom: corner % 2 === 1 ? 10 : undefined,
                  width: 0,
                  height: 0,
                  borderLeftWidth: corner >= 2 ? 30 : 0,
                  borderRightWidth: corner < 2 ? 30 : 0,
                  borderBottomWidth: corner % 2 === 0 ? 30 : 0,
                  borderTopWidth: corner % 2 === 1 ? 30 : 0,
                  borderLeftColor: corner >= 2 ? background.patternColor : 'transparent',
                  borderRightColor: corner < 2 ? background.patternColor : 'transparent',
                  borderBottomColor: corner % 2 === 0 ? 'transparent' : 'transparent',
                  borderTopColor: corner % 2 === 1 ? 'transparent' : 'transparent',
                  opacity: 0.3,
                }}
              />
            ))}
          </View>
        );

      case 'rustic':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {/* Rustic wood planks */}
            {[...Array(Math.ceil(height / 40))].map((_, index) => (
              <View key={index}>
                <View
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: index * 40,
                    width: width,
                    height: 38,
                    backgroundColor: index % 2 === 0 ? background.baseColor : background.secondaryColor,
                  }}
                />
                <View
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: index * 40 + 38,
                    width: width,
                    height: 2,
                    backgroundColor: '#000000',
                    opacity: 0.2,
                  }}
                />
              </View>
            ))}
          </View>
        );

      case 'minimal':
        return (
          <View style={StyleSheet.absoluteFill}>
            <View style={{ backgroundColor: background.baseColor, ...StyleSheet.absoluteFillObject }} />
            {/* Subtle border */}
            <View
              style={{
                position: 'absolute',
                left: 30,
                top: 30,
                right: 30,
                bottom: 30,
                borderWidth: 1,
                borderColor: background.borderColor,
                opacity: 0.3,
              }}
            />
          </View>
        );

      default:
        return (
          <View style={[StyleSheet.absoluteFill, { backgroundColor: background.baseColor || background.color }]} />
        );
    }
  };

  return renderPattern();
};

// Predefined layout templates
const LAYOUT_TEMPLATES = {
  classic: {
    name: 'Classic',
    icon: 'view-agenda',
    productStyle: {
      layout: 'horizontal',
      showImage: true,
      showDescription: true,
      showVolume: true,
      imageSize: 80,
      fontSize: 16,
      padding: 10,
    },
  },
  compact: {
    name: 'Compact',
    icon: 'view-list',
    productStyle: {
      layout: 'horizontal',
      showImage: false,
      showDescription: false,
      showVolume: true,
      imageSize: 0,
      fontSize: 14,
      padding: 5,
    },
  },
  grid: {
    name: 'Grid',
    icon: 'view-module',
    productStyle: {
      layout: 'vertical',
      showImage: true,
      showDescription: true,
      showVolume: true,
      imageSize: 120,
      fontSize: 14,
      padding: 8,
    },
  },
  elegant: {
    name: 'Elegant',
    icon: 'auto-awesome',
    productStyle: {
      layout: 'vertical',
      showImage: true,
      showDescription: true,
      showVolume: true,
      imageSize: 100,
      fontSize: 18,
      padding: 15,
      fontFamily: 'serif',
    },
  },
  minimal: {
    name: 'Minimal',
    icon: 'minimize',
    productStyle: {
      layout: 'horizontal',
      showImage: false,
      showDescription: false,
      showVolume: false,
      imageSize: 0,
      fontSize: 16,
      padding: 8,
    },
  },
  imageOnly: {
    name: 'Image Only',
    icon: 'image',
    productStyle: {
      layout: 'vertical',
      showImage: true,
      showDescription: false,
      showVolume: false,
      imageSize: 150,
      fontSize: 14,
      padding: 5,
    },
  },
  priceList: {
    name: 'Price List',
    icon: 'format-list-numbered',
    productStyle: {
      layout: 'horizontal',
      showImage: false,
      showDescription: false,
      showVolume: true,
      imageSize: 0,
      fontSize: 12,
      padding: 3,
    },
  },
  magazine: {
    name: 'Magazine',
    icon: 'article',
    productStyle: {
      layout: 'horizontal',
      showImage: true,
      showDescription: true,
      showVolume: true,
      imageSize: 100,
      fontSize: 15,
      padding: 12,
    },
  },
  cards: {
    name: 'Cards',
    icon: 'dashboard',
    productStyle: {
      layout: 'vertical',
      showImage: true,
      showDescription: true,
      showVolume: true,
      imageSize: 90,
      fontSize: 13,
      padding: 10,
    },
  },
  luxe: {
    name: 'Luxe',
    icon: 'stars',
    productStyle: {
      layout: 'vertical',
      showImage: true,
      showDescription: true,
      showVolume: true,
      imageSize: 110,
      fontSize: 20,
      padding: 20,
      fontFamily: 'serif',
    },
  },
  tiny: {
    name: 'Tiny',
    icon: 'view-headline',
    productStyle: {
      layout: 'horizontal',
      showImage: false,
      showDescription: false,
      showVolume: false,
      imageSize: 0,
      fontSize: 10,
      padding: 2,
    },
  },
  thumbnail: {
    name: 'Thumbnail',
    icon: 'photo-size-select-small',
    productStyle: {
      layout: 'horizontal',
      showImage: true,
      showDescription: false,
      showVolume: false,
      imageSize: 40,
      fontSize: 12,
      padding: 4,
    },
  },
};

// Text style presets
const TEXT_STYLES = {
  title: { fontSize: 32, fontWeight: 'bold', color: '#000000' },
  subtitle: { fontSize: 24, fontWeight: '600', color: '#333333' },
  heading: { fontSize: 20, fontWeight: '600', color: '#444444' },
  body: { fontSize: 16, fontWeight: 'normal', color: '#555555' },
  price: { fontSize: 18, fontWeight: 'bold', color: '#000000' },
  accent: { fontSize: 16, fontWeight: '500', color: '#C00000' },
};

// Default product text styles
const DEFAULT_PRODUCT_STYLES = {
  title: { fontSize: 16, fontWeight: '600', color: '#000000' },
  description: { fontSize: 14, fontWeight: 'normal', color: '#666666' },
  variant: { fontSize: 12, fontWeight: 'normal', color: '#888888' },
  price: { fontSize: 16, fontWeight: 'bold', color: '#000000' },
  background: { color: 'transparent' },
  showAllergens: true,
};

// Default border styles for elements
const DEFAULT_BORDER_STYLES = {
  width: 0,
  color: '#000000',
  style: 'solid', // solid, dashed, dotted
  radius: 0,
};

const BORDER_STYLE_OPTIONS = ['none', 'solid', 'dashed', 'dotted'];
const BORDER_WIDTH_OPTIONS = [0, 1, 2, 3, 4, 5];
const BORDER_RADIUS_OPTIONS = [0, 4, 8, 12, 16, 20, 24, 32];

// Menu language options
const MENU_LANGUAGES = [
  { code: 'original', name: 'Original', flag: '🌐' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
];

// AI Design Proposals
const AI_DESIGN_PROPOSALS = [
  {
    id: 'elegant-cafe',
    name: 'Elegant Cafe',
    description: 'Sophisticated dark theme with golden accents',
    preview: { background: '#1a1a2e', accent: '#d4af37' },
    background: { id: 'dark-elegant', baseColor: '#1a1a2e', category: 'solid' },
    layoutTemplate: 'elegant',
    productStyles: {
      title: { fontSize: 18, fontWeight: '600', color: '#d4af37' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#cccccc' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#999999' },
      price: { fontSize: 18, fontWeight: 'bold', color: '#ffffff' },
    },
    borderStyles: { width: 1, color: '#d4af37', style: 'solid', radius: 8 },
    productScale: 1.1,
  },
  {
    id: 'modern-minimal',
    name: 'Modern Minimal',
    description: 'Clean white design with subtle shadows',
    preview: { background: '#ffffff', accent: '#007AFF' },
    background: { id: 'modern-white', baseColor: '#ffffff', category: 'solid' },
    layoutTemplate: 'minimal',
    productStyles: {
      title: { fontSize: 16, fontWeight: '500', color: '#1a1a1a' },
      description: { fontSize: 13, fontWeight: 'normal', color: '#666666' },
      variant: { fontSize: 11, fontWeight: 'normal', color: '#999999' },
      price: { fontSize: 16, fontWeight: '600', color: '#007AFF' },
    },
    productScale: 1.0,
  },
  {
    id: 'rustic-bistro',
    name: 'Rustic Bistro',
    description: 'Warm wood textures with earthy tones',
    preview: { background: '#8B4513', accent: '#F5DEB3' },
    background: { id: 'wood', baseColor: '#8B4513', secondaryColor: '#654321', pattern: 'wood', category: 'textures' },
    layoutTemplate: 'classic',
    productStyles: {
      title: { fontSize: 18, fontWeight: 'bold', color: '#3d2914' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#5c4033' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#7d6048' },
      price: { fontSize: 17, fontWeight: 'bold', color: '#8B0000' },
    },
    productScale: 1.0,
  },
  {
    id: 'ocean-breeze',
    name: 'Ocean Breeze',
    description: 'Calming blue gradient with fresh feel',
    preview: { background: '#1e3c72', accent: '#87CEEB' },
    background: { id: 'ocean', baseColor: '#1e3c72', gradientColors: ['#1e3c72', '#2a5298', '#87CEEB'], pattern: 'gradient-vertical', category: 'gradients' },
    layoutTemplate: 'cards',
    productStyles: {
      title: { fontSize: 17, fontWeight: '600', color: '#ffffff' },
      description: { fontSize: 13, fontWeight: 'normal', color: '#e0e0e0' },
      variant: { fontSize: 11, fontWeight: 'normal', color: '#b0c4de' },
      price: { fontSize: 18, fontWeight: 'bold', color: '#FFD700' },
    },
    productScale: 1.0,
  },
  {
    id: 'vintage-charm',
    name: 'Vintage Charm',
    description: 'Classic sepia tones with decorative borders',
    preview: { background: '#F5F5DC', accent: '#8B4513' },
    background: { id: 'vintage', baseColor: '#F5F5DC', borderColor: '#8B4513', pattern: 'vintage', category: 'artistic' },
    layoutTemplate: 'luxe',
    productStyles: {
      title: { fontSize: 20, fontWeight: 'bold', color: '#4a3728' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#6b5344' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#8b7355' },
      price: { fontSize: 18, fontWeight: 'bold', color: '#8B0000' },
    },
    borderStyles: { width: 2, color: '#8B4513', style: 'dashed', radius: 4 },
    productScale: 1.2,
  },
  {
    id: 'neon-nights',
    name: 'Neon Nights',
    description: 'Bold dark theme with vibrant neon accents',
    preview: { background: '#0a0a0a', accent: '#ff00ff' },
    background: { id: 'neon', baseColor: '#0a0a0a', patternColor: '#ff00ff', pattern: 'neon', category: 'artistic' },
    layoutTemplate: 'grid',
    productStyles: {
      title: { fontSize: 16, fontWeight: 'bold', color: '#00ffff' },
      description: { fontSize: 13, fontWeight: 'normal', color: '#888888' },
      variant: { fontSize: 11, fontWeight: 'normal', color: '#666666' },
      price: { fontSize: 18, fontWeight: 'bold', color: '#ff00ff' },
    },
    borderStyles: { width: 2, color: '#ff00ff', style: 'solid', radius: 0 },
    productScale: 0.9,
  },
  {
    id: 'garden-fresh',
    name: 'Garden Fresh',
    description: 'Natural green tones for healthy menus',
    preview: { background: '#f0fff0', accent: '#228B22' },
    background: { id: 'garden', baseColor: '#f0fff0', gradientColors: ['#f0fff0', '#98FB98', '#90EE90'], pattern: 'gradient-vertical', category: 'gradients' },
    layoutTemplate: 'magazine',
    productStyles: {
      title: { fontSize: 17, fontWeight: '600', color: '#1e5631' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#3d7c47' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#5a9e6f' },
      price: { fontSize: 16, fontWeight: 'bold', color: '#006400' },
    },
    productScale: 1.0,
  },
  {
    id: 'marble-luxe',
    name: 'Marble Luxe',
    description: 'Premium marble texture with gold details',
    preview: { background: '#fafafa', accent: '#d4af37' },
    background: { id: 'marble-white', baseColor: '#fafafa', secondaryColor: '#c0c0c0', pattern: 'marble', category: 'textures' },
    layoutTemplate: 'elegant',
    productStyles: {
      title: { fontSize: 19, fontWeight: '500', color: '#2c2c2c' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#555555' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#777777' },
      price: { fontSize: 18, fontWeight: 'bold', color: '#d4af37' },
    },
    borderStyles: { width: 1, color: '#d4af37', style: 'solid', radius: 12 },
    productScale: 1.1,
  },
  {
    id: 'sunset-warmth',
    name: 'Sunset Warmth',
    description: 'Warm orange to pink gradient',
    preview: { background: '#ff6b6b', accent: '#ffd93d' },
    background: { id: 'sunset', baseColor: '#ff6b6b', gradientColors: ['#ff6b6b', '#ff8e53', '#ffd93d'], pattern: 'gradient-vertical', category: 'gradients' },
    layoutTemplate: 'cards',
    productStyles: {
      title: { fontSize: 17, fontWeight: 'bold', color: '#ffffff' },
      description: { fontSize: 13, fontWeight: 'normal', color: '#fff5e6' },
      variant: { fontSize: 11, fontWeight: 'normal', color: '#ffe0cc' },
      price: { fontSize: 18, fontWeight: 'bold', color: '#4a0000' },
    },
    productScale: 1.0,
  },
  {
    id: 'nordic-frost',
    name: 'Nordic Frost',
    description: 'Cool ice blue minimalist design',
    preview: { background: '#e8f4f8', accent: '#5c9ead' },
    background: { id: 'frost', baseColor: '#e8f4f8', category: 'solid' },
    layoutTemplate: 'minimal',
    productStyles: {
      title: { fontSize: 16, fontWeight: '500', color: '#2c3e50' },
      description: { fontSize: 13, fontWeight: 'normal', color: '#5c9ead' },
      variant: { fontSize: 11, fontWeight: 'normal', color: '#7fb3c0' },
      price: { fontSize: 16, fontWeight: '600', color: '#34495e' },
    },
    productScale: 1.0,
  },
  {
    id: 'cherry-blossom',
    name: 'Cherry Blossom',
    description: 'Soft pink Japanese-inspired theme',
    preview: { background: '#fff0f5', accent: '#ff69b4' },
    background: { id: 'blossom', baseColor: '#fff0f5', gradientColors: ['#fff0f5', '#ffb6c1', '#ffc0cb'], pattern: 'gradient-vertical', category: 'gradients' },
    layoutTemplate: 'elegant',
    productStyles: {
      title: { fontSize: 18, fontWeight: '500', color: '#8b0a50' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#cd5c5c' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#db7093' },
      price: { fontSize: 17, fontWeight: 'bold', color: '#c71585' },
    },
    borderStyles: { width: 1, color: '#ff69b4', style: 'dotted', radius: 16 },
    productScale: 1.1,
  },
  {
    id: 'midnight-purple',
    name: 'Midnight Purple',
    description: 'Deep purple with mystical vibes',
    preview: { background: '#1a0a2e', accent: '#9d4edd' },
    background: { id: 'midnight', baseColor: '#1a0a2e', gradientColors: ['#1a0a2e', '#2d1b4e', '#432874'], pattern: 'gradient-vertical', category: 'gradients' },
    layoutTemplate: 'luxe',
    productStyles: {
      title: { fontSize: 19, fontWeight: '600', color: '#e0aaff' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#c77dff' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#9d4edd' },
      price: { fontSize: 18, fontWeight: 'bold', color: '#ffffff' },
    },
    productScale: 1.2,
  },
  {
    id: 'coffee-house',
    name: 'Coffee House',
    description: 'Rich brown coffee shop aesthetic',
    preview: { background: '#3c2415', accent: '#d4a574' },
    background: { id: 'coffee', baseColor: '#3c2415', secondaryColor: '#2a1810', pattern: 'wood', category: 'textures' },
    layoutTemplate: 'classic',
    productStyles: {
      title: { fontSize: 17, fontWeight: 'bold', color: '#f5e6d3' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#d4a574' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#c49a6c' },
      price: { fontSize: 17, fontWeight: 'bold', color: '#ffd700' },
    },
    productScale: 1.0,
  },
  {
    id: 'tropical-paradise',
    name: 'Tropical Paradise',
    description: 'Vibrant teal and coral beach vibes',
    preview: { background: '#20b2aa', accent: '#ff7f50' },
    background: { id: 'tropical', baseColor: '#20b2aa', gradientColors: ['#008b8b', '#20b2aa', '#48d1cc'], pattern: 'gradient-vertical', category: 'gradients' },
    layoutTemplate: 'magazine',
    productStyles: {
      title: { fontSize: 17, fontWeight: 'bold', color: '#ffffff' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#e0ffff' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#afeeee' },
      price: { fontSize: 18, fontWeight: 'bold', color: '#ff7f50' },
    },
    productScale: 1.0,
  },
  {
    id: 'monochrome-chic',
    name: 'Monochrome Chic',
    description: 'Stylish black and white contrast',
    preview: { background: '#ffffff', accent: '#000000' },
    background: { id: 'mono', baseColor: '#ffffff', patternColor: '#e0e0e0', pattern: 'stripes', category: 'patterns' },
    layoutTemplate: 'compact',
    productStyles: {
      title: { fontSize: 15, fontWeight: 'bold', color: '#000000' },
      description: { fontSize: 13, fontWeight: 'normal', color: '#444444' },
      variant: { fontSize: 11, fontWeight: 'normal', color: '#666666' },
      price: { fontSize: 16, fontWeight: 'bold', color: '#000000' },
    },
    productScale: 0.9,
  },
  {
    id: 'autumn-harvest',
    name: 'Autumn Harvest',
    description: 'Warm fall colors with rustic feel',
    preview: { background: '#8b4000', accent: '#ffa500' },
    background: { id: 'autumn', baseColor: '#8b4000', gradientColors: ['#8b4000', '#cd6600', '#daa520'], pattern: 'gradient-vertical', category: 'gradients' },
    layoutTemplate: 'classic',
    productStyles: {
      title: { fontSize: 18, fontWeight: 'bold', color: '#fff8dc' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#ffe4b5' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#ffdead' },
      price: { fontSize: 17, fontWeight: 'bold', color: '#ffff00' },
    },
    productScale: 1.0,
  },
  {
    id: 'lavender-dream',
    name: 'Lavender Dream',
    description: 'Soft purple calming atmosphere',
    preview: { background: '#e6e6fa', accent: '#9370db' },
    background: { id: 'lavender', baseColor: '#e6e6fa', category: 'solid' },
    layoutTemplate: 'elegant',
    productStyles: {
      title: { fontSize: 18, fontWeight: '500', color: '#4b0082' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#663399' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#9370db' },
      price: { fontSize: 17, fontWeight: 'bold', color: '#8b008b' },
    },
    productScale: 1.1,
  },
  {
    id: 'industrial-steel',
    name: 'Industrial Steel',
    description: 'Modern industrial gray metal look',
    preview: { background: '#2f3640', accent: '#718093' },
    background: { id: 'steel', baseColor: '#2f3640', secondaryColor: '#353b48', pattern: 'brushed', category: 'textures' },
    layoutTemplate: 'compact',
    productStyles: {
      title: { fontSize: 15, fontWeight: '600', color: '#dcdde1' },
      description: { fontSize: 13, fontWeight: 'normal', color: '#718093' },
      variant: { fontSize: 11, fontWeight: 'normal', color: '#7f8fa6' },
      price: { fontSize: 16, fontWeight: 'bold', color: '#fbc531' },
    },
    productScale: 0.95,
  },
  {
    id: 'royal-blue',
    name: 'Royal Blue',
    description: 'Regal deep blue with gold accents',
    preview: { background: '#002366', accent: '#ffd700' },
    background: { id: 'royal', baseColor: '#002366', category: 'solid' },
    layoutTemplate: 'luxe',
    productStyles: {
      title: { fontSize: 20, fontWeight: 'bold', color: '#ffd700' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#b8860b' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#daa520' },
      price: { fontSize: 19, fontWeight: 'bold', color: '#ffffff' },
    },
    borderStyles: { width: 2, color: '#ffd700', style: 'solid', radius: 4 },
    productScale: 1.2,
  },
  {
    id: 'mint-fresh',
    name: 'Mint Fresh',
    description: 'Cool mint green refreshing style',
    preview: { background: '#f5fffa', accent: '#3cb371' },
    background: { id: 'mint', baseColor: '#f5fffa', patternColor: '#98fb98', pattern: 'dots', category: 'patterns' },
    layoutTemplate: 'grid',
    productStyles: {
      title: { fontSize: 16, fontWeight: '600', color: '#2e8b57' },
      description: { fontSize: 13, fontWeight: 'normal', color: '#3cb371' },
      variant: { fontSize: 11, fontWeight: 'normal', color: '#66cdaa' },
      price: { fontSize: 17, fontWeight: 'bold', color: '#006400' },
    },
    productScale: 1.0,
  },
  {
    id: 'newspaper-classic',
    name: 'Newspaper Classic',
    description: 'Retro newspaper print style',
    preview: { background: '#f5f5dc', accent: '#2f2f2f' },
    background: { id: 'news', baseColor: '#f5f5dc', secondaryColor: '#333333', pattern: 'newspaper', category: 'artistic' },
    layoutTemplate: 'priceList',
    productStyles: {
      title: { fontSize: 14, fontWeight: 'bold', color: '#1a1a1a' },
      description: { fontSize: 12, fontWeight: 'normal', color: '#4a4a4a' },
      variant: { fontSize: 10, fontWeight: 'normal', color: '#6a6a6a' },
      price: { fontSize: 14, fontWeight: 'bold', color: '#1a1a1a' },
    },
    borderStyles: { width: 1, color: '#1a1a1a', style: 'dashed', radius: 0 },
    productScale: 0.85,
  },
  {
    id: 'candy-pop',
    name: 'Candy Pop',
    description: 'Fun colorful candy store theme',
    preview: { background: '#ff6b9d', accent: '#c9f0ff' },
    background: { id: 'candy', baseColor: '#ff6b9d', gradientColors: ['#ff6b9d', '#ffa8cc', '#c9f0ff'], pattern: 'gradient-vertical', category: 'gradients' },
    layoutTemplate: 'cards',
    productStyles: {
      title: { fontSize: 17, fontWeight: 'bold', color: '#ffffff' },
      description: { fontSize: 13, fontWeight: 'normal', color: '#fff0f5' },
      variant: { fontSize: 11, fontWeight: 'normal', color: '#ffe4ec' },
      price: { fontSize: 18, fontWeight: 'bold', color: '#4a0072' },
    },
    productScale: 1.0,
  },
  {
    id: 'earth-tones',
    name: 'Earth Tones',
    description: 'Natural earthy browns and greens',
    preview: { background: '#5d4037', accent: '#a1887f' },
    background: { id: 'earth', baseColor: '#5d4037', secondaryColor: '#4e342e', pattern: 'rustic', category: 'artistic' },
    layoutTemplate: 'classic',
    productStyles: {
      title: { fontSize: 17, fontWeight: '600', color: '#efebe9' },
      description: { fontSize: 14, fontWeight: 'normal', color: '#d7ccc8' },
      variant: { fontSize: 12, fontWeight: 'normal', color: '#bcaaa4' },
      price: { fontSize: 17, fontWeight: 'bold', color: '#ffcc80' },
    },
    productScale: 1.0,
  },
];

// Draggable Element Component
const DraggableElement = ({ element, elements, onUpdate, onSelect, isSelected, selectedElementIds = [], canvasSize, productStyles, productScale = 1.0, borderStyles, menuLanguage = 'original', onDragStart, onDragEnd, onMoveSelected, background, onDoubleClick }) => {
  const pan = useRef(new Animated.ValueXY({ x: element.x, y: element.y })).current;
  const scale = useRef(new Animated.Value(1)).current;
  const lastTapRef = useRef(0);
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const resizeStartSize = useRef({ width: 0, height: 0 });
  const isResizableElement = element.type === 'image' || element.type === 'divider';

  // Merge default styles with custom product styles
  const pStyles = productStyles || DEFAULT_PRODUCT_STYLES;
  const bStyles = borderStyles || DEFAULT_BORDER_STYLES;

  // Translation helper based on menu language
  const translateText = (text) => {
    if (!text) return text;
    if (menuLanguage === 'original') return text;
    if (menuLanguage === 'en') return i18n.t(text);
    // For other languages, return original (could be extended with more translations)
    return text;
  };
  const pScale = productScale || 1.0;

  // Refs for latest values (needed because PanResponder captures initial values)
  const selectedIdsRef = useRef(selectedElementIds);
  const onMoveSelectedRef = useRef(onMoveSelected);
  const onSelectRef = useRef(onSelect);
  const isSelectedRef = useRef(isSelected);
  const startPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    selectedIdsRef.current = selectedElementIds;
    onMoveSelectedRef.current = onMoveSelected;
    onSelectRef.current = onSelect;
    isSelectedRef.current = isSelected;
  }, [selectedElementIds, onMoveSelected, onSelect, isSelected]);

  useEffect(() => {
    pan.setValue({ x: element.x, y: element.y });
  }, [element.x, element.y]);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onStartShouldSetPanResponderCapture: () => true,
      onMoveShouldSetPanResponderCapture: () => true,
      onPanResponderTerminationRequest: () => false, // Don't allow ScrollView to take over
      onShouldBlockNativeResponder: () => true, // Block native scroll gesture on iOS
      onPanResponderGrant: (e) => {
        const now = Date.now();
        const DOUBLE_TAP_DELAY = 300;

        if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
          // Double tap detected
          if (onDoubleClick) {
            onDoubleClick(element);
          }
          lastTapRef.current = 0; // Reset to prevent triple-tap
        } else {
          lastTapRef.current = now;
        }

        // Check for shift key (web only) for multi-select
        const addToSelection = Platform.OS === 'web' && e.nativeEvent?.shiftKey;
        onSelectRef.current(element.id, addToSelection);

        // Store starting position for multi-select movement
        startPosRef.current = { x: element.x, y: element.y };

        pan.setOffset({ x: pan.x._value, y: pan.y._value });
        pan.setValue({ x: 0, y: 0 });
        Animated.spring(scale, { toValue: 1.05, useNativeDriver: false }).start();
        if (onDragStart) onDragStart();
      },
      onPanResponderMove: Animated.event([null, { dx: pan.x, dy: pan.y }], {
        useNativeDriver: false,
      }),
      onPanResponderRelease: (e, gesture) => {
        pan.flattenOffset();
        Animated.spring(scale, { toValue: 1, useNativeDriver: false }).start();

        // Constrain to canvas bounds - allow elements to go partially off-canvas
        // Keep at least 20px of the element visible on each edge
        const minVisible = 20;
        const elemWidth = element.width || 100;
        const elemHeight = element.height || 50;

        const minX = -elemWidth + minVisible;
        const maxX = canvasSize.width - minVisible;
        const minY = -elemHeight + minVisible;
        const maxY = canvasSize.height - minVisible;

        const newX = Math.max(minX, Math.min(pan.x._value, maxX));
        const newY = Math.max(minY, Math.min(pan.y._value, maxY));

        // Calculate delta movement
        const dx = newX - startPosRef.current.x;
        const dy = newY - startPosRef.current.y;

        // If multiple elements selected and this element is selected, move all
        if (selectedIdsRef.current.length > 1 && selectedIdsRef.current.includes(element.id)) {
          if (onMoveSelectedRef.current) {
            onMoveSelectedRef.current(dx, dy);
          }
        } else {
          onUpdate(element.id, { x: newX, y: newY });
        }

        if (onDragEnd) onDragEnd();
      },
      onPanResponderTerminate: () => {
        if (onDragEnd) onDragEnd();
      },
    })
  ).current;

  // Resize handling using direct mouse/touch events (works better than PanResponder for nested elements)
  const resizeStartPos = useRef({ x: 0, y: 0 });

  const handleResizeStart = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();

    isResizingRef.current = true;
    setIsResizing(true);
    resizeStartSize.current = {
      width: element.width || 100,
      height: element.height || 100,
    };

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    resizeStartPos.current = { x: clientX, y: clientY };

    if (onDragStart) onDragStart();

    const handleMove = (moveEvent) => {
      if (!isResizingRef.current) return;

      const moveX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const moveY = moveEvent.touches ? moveEvent.touches[0].clientY : moveEvent.clientY;

      const dx = moveX - resizeStartPos.current.x;
      const dy = moveY - resizeStartPos.current.y;

      const aspectRatio = element.data?.aspectRatio;
      let newWidth = Math.max(30, resizeStartSize.current.width + dx);
      let newHeight;

      if (aspectRatio) {
        newHeight = newWidth / aspectRatio;
      } else {
        newHeight = Math.max(30, resizeStartSize.current.height + dy);
      }

      onUpdate(element.id, { width: newWidth, height: newHeight });
    };

    const handleEnd = () => {
      isResizingRef.current = false;
      setIsResizing(false);
      if (onDragEnd) onDragEnd();

      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };

    // Add document-level listeners for move and end
    if (Platform.OS === 'web') {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleEnd);
    }
  }, [element.width, element.height, element.data?.aspectRatio, element.id, onUpdate, onDragStart, onDragEnd]);

  const renderContent = () => {
    // Apply scale to sizes
    const scaledImageSize = (element.data.imageSize || 80) * pScale;
    const isVertical = element.data.layout === 'vertical';
    const padding = (element.data.padding || 8) * pScale;

    // Compute border styles - only apply if width > 0 and style is not 'none'
    const borderStyleObj = bStyles.width > 0 && bStyles.style !== 'none' ? {
      borderWidth: bStyles.width,
      borderColor: bStyles.color,
      borderStyle: bStyles.style,
      borderRadius: bStyles.radius,
    } : {};

    switch (element.type) {
      case 'product':
        // Check for element-specific background, fall back to global style
        const elementBgColor = element.data?.elementStyles?.background?.color;
        const productBgColor = (elementBgColor && elementBgColor !== 'inherit')
          ? elementBgColor
          : (pStyles.background?.color || 'transparent');
        if (isVertical) {
          // Vertical layout: image on top, text below, price at bottom
          return (
            <View
              pointerEvents="none"
              style={[
                element.style,
                {
                  flexDirection: 'column',
                  alignItems: 'center',
                  backgroundColor: productBgColor,
                  padding: padding,
                },
                borderStyleObj,
              ]}>
              {element.data.showImage && element.data.imageUrl && (
                <ProductImage
                  uri={getProductImageUrl(element.data.imageUrl)}
                  resizeMode="contain"
                  style={{
                    width: scaledImageSize,
                    height: scaledImageSize,
                    borderRadius: 8 * pScale,
                    marginBottom: 8 * pScale,
                  }}
                />
              )}
              <Text style={[
                styles.productTitle,
                {
                  fontSize: pStyles.title.fontSize * pScale,
                  fontWeight: pStyles.title.fontWeight,
                  color: pStyles.title.color,
                  textAlign: 'center',
                }
              ]}>
                {translateText(element.data.title)}
              </Text>
              {element.data.showDescription && element.data.subtitle && (
                <Text style={[
                  styles.productSubtitle,
                  {
                    fontSize: pStyles.description.fontSize * pScale,
                    fontWeight: pStyles.description.fontWeight,
                    color: pStyles.description.color,
                    marginTop: 4 * pScale,
                    textAlign: 'center',
                  }
                ]}>
                  {translateText(element.data.subtitle)}
                </Text>
              )}
              {element.data.showVolume && element.data.volume && (
                <Text style={[
                  styles.productVolume,
                  {
                    fontSize: pStyles.variant.fontSize * pScale,
                    fontWeight: pStyles.variant.fontWeight,
                    color: pStyles.variant.color,
                    marginTop: 2 * pScale,
                    textAlign: 'center',
                  }
                ]}>
                  {element.data.volume}
                </Text>
              )}
              <Text style={[
                styles.productPrice,
                {
                  fontSize: pStyles.price.fontSize * pScale,
                  fontWeight: pStyles.price.fontWeight,
                  color: pStyles.price.color,
                  marginTop: 8 * pScale,
                }
              ]}>
                {element.data.price}
              </Text>
              {pStyles.showAllergens !== false && element.data.allergens && element.data.allergens.length > 0 && (
                <AllergenBadges
                  allergens={element.data.allergens}
                  size={10 * pScale}
                  style={{ marginTop: 6 * pScale, justifyContent: 'center' }}
                />
              )}
            </View>
          );
        }
        // Horizontal layout: image on left, text in middle, price on right
        return (
          <View
            pointerEvents="none"
            style={[
              styles.productElement,
              element.style,
              {
                backgroundColor: productBgColor,
                padding: padding,
              },
              borderStyleObj,
            ]}>
            {element.data.showImage && element.data.imageUrl && (
              <ProductImage
                uri={getProductImageUrl(element.data.imageUrl)}
                resizeMode="contain"
                style={{
                  width: scaledImageSize,
                  height: scaledImageSize,
                  borderRadius: 8 * pScale,
                }}
              />
            )}
            <View style={{ flex: 1, marginLeft: element.data.showImage ? 10 * pScale : 0, backgroundColor: 'transparent' }}>
              <Text style={[
                styles.productTitle,
                {
                  fontSize: pStyles.title.fontSize * pScale,
                  fontWeight: pStyles.title.fontWeight,
                  color: pStyles.title.color,
                }
              ]}>
                {translateText(element.data.title)}
              </Text>
              {element.data.showDescription && element.data.subtitle && (
                <Text style={[
                  styles.productSubtitle,
                  {
                    fontSize: pStyles.description.fontSize * pScale,
                    fontWeight: pStyles.description.fontWeight,
                    color: pStyles.description.color,
                    marginTop: 2 * pScale,
                  }
                ]}>
                  {translateText(element.data.subtitle)}
                </Text>
              )}
              {element.data.showVolume && element.data.volume && (
                <Text style={[
                  styles.productVolume,
                  {
                    fontSize: pStyles.variant.fontSize * pScale,
                    fontWeight: pStyles.variant.fontWeight,
                    color: pStyles.variant.color,
                    marginTop: 2 * pScale,
                  }
                ]}>
                  {element.data.volume}
                </Text>
              )}
              {pStyles.showAllergens !== false && element.data.allergens && element.data.allergens.length > 0 && (
                <AllergenBadges
                  allergens={element.data.allergens}
                  size={10 * pScale}
                  style={{ marginTop: 4 * pScale }}
                />
              )}
            </View>
            <Text style={[
              styles.productPrice,
              {
                fontSize: pStyles.price.fontSize * pScale,
                fontWeight: pStyles.price.fontWeight,
                color: pStyles.price.color,
                marginLeft: 12 * pScale,
              }
            ]}>
              {element.data.price}
            </Text>
          </View>
        );
      case 'text':
        const textBgColor = element.style?.backgroundColor || 'transparent';
        const textPadding = element.style?.padding || 0;
        const textBorderRadius = element.style?.borderRadius || 0;
        return (
          <View
            style={[
              {
                padding: textPadding || (bStyles.width > 0 ? 4 : 0),
                backgroundColor: textBgColor === 'transparent' ? 'rgba(0,0,0,0)' : textBgColor,
                borderRadius: textBorderRadius,
              },
              borderStyleObj,
            ]}
            pointerEvents="none"
          >
            <Text
              style={[
                {
                  fontSize: element.style?.fontSize || 16,
                  fontWeight: element.style?.fontWeight || 'normal',
                  color: element.style?.color || '#000000',
                  fontFamily: element.style?.fontFamily,
                  textAlign: element.style?.textAlign || 'left',
                },
              ]}
            >
              {element.data.text}
            </Text>
          </View>
        );
      case 'image':
        const imgWidth = element.width || 100;
        const imgHeight = element.height || 100;
        const imgBorderRadius = bStyles.radius > 0 ? bStyles.radius : (element.style?.borderRadius || 0);

        if (Platform.OS === 'web') {
          return (
            <View style={[borderStyleObj, { backgroundColor: 'transparent' }]} pointerEvents="none">
              <img
                src={element.data.uri}
                alt=""
                data-product-image={element.data.uri}
                style={{
                  width: `${imgWidth}px`,
                  height: `${imgHeight}px`,
                  borderRadius: `${imgBorderRadius}px`,
                  objectFit: 'contain',
                }}
              />
            </View>
          );
        }
        return (
          <View style={[borderStyleObj, { backgroundColor: 'transparent' }]} pointerEvents="none">
            <Image
              source={{ uri: element.data.uri, cache: 'default' }}
              style={{
                width: imgWidth,
                height: imgHeight,
                borderRadius: imgBorderRadius,
              }}
              resizeMode="contain"
            />
          </View>
        );
      case 'divider':
        return (
          <View
            pointerEvents="none"
            style={[{
              width: element.width || 200,
              height: element.style?.thickness || 2,
              backgroundColor: element.style?.color || '#000000',
            }, borderStyleObj]}
          />
        );
      case 'allergen-legend':
        // Get products from the design elements to build the legend
        // Wrap allergens in array since buildAllergenLegend expects array of arrays (per variant)
        const productElements = elements.filter(e => e.type === 'product');
        const productsForLegend = productElements.map(e => ({
          allergens: [e.data.allergens || []]
        }));
        const legendColorScheme = background?.textColor === '#FFFFFF' ? 'dark' : 'light';
        const legendStyle = element.data?.legendStyle || {};
        return (
          <View style={[{ width: element.width || 200, backgroundColor: 'rgba(0,0,0,0)' }, borderStyleObj]} pointerEvents="none">
            <AllergenLegend
              products={productsForLegend}
              colorScheme={legendColorScheme}
              showTitle={legendStyle.showTitle}
              showBorder={legendStyle.showBorder}
              circleSize={legendStyle.circleSize}
              titleStyle={legendStyle.titleStyle}
              labelStyle={legendStyle.labelStyle}
              backgroundColor={legendStyle.backgroundColor}
              language={menuLanguage}
            />
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <Animated.View
      {...panResponder.panHandlers}
      dataSet={{ draggable: 'true' }}
      style={[
        styles.draggableElement,
        {
          transform: [{ translateX: pan.x }, { translateY: pan.y }, { scale: isResizing ? 1 : scale }],
          borderColor: isSelected ? '#007AFF' : 'transparent',
          borderWidth: isSelected ? 2 : 0,
          zIndex: isSelected ? 1000 : element.zIndex || 1,
        },
      ]}
    >
      {/* pointerEvents="none" ensures all touch events pass through to the parent
          Animated.View's PanResponder, enabling drag from anywhere on the element */}
      <View pointerEvents="none" style={{ backgroundColor: 'transparent' }}>
        {renderContent()}
      </View>
      {isSelected && (
        <View style={styles.resizeHandle} pointerEvents="none">
          <MaterialIcons name="open-with" size={16} color="#007AFF" />
        </View>
      )}
      {/* Resize handle for image/divider elements */}
      {isSelected && isResizableElement && (
        <View
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          style={styles.cornerResizeHandle}
        >
          <MaterialIcons name="open-in-full" size={14} color="#fff" />
        </View>
      )}
    </Animated.View>
  );
};

// Product Search Modal
const ProductSearchModal = ({ visible, onClose, onSelectProduct, products, layoutTemplate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const colorScheme = useColorScheme();

  const filteredProducts = products?.filter(
    (p) =>
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={[styles.modalContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7' }]}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Add Product</Text>
          <TouchableOpacity onPress={onClose}>
            <MaterialIcons name="close" size={28} color={colorScheme === 'dark' ? '#fff' : '#000'} />
          </TouchableOpacity>
        </View>

        <TextInput
          style={[styles.searchInput, {
            backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff',
            color: colorScheme === 'dark' ? '#fff' : '#000'
          }]}
          placeholder="Search products..."
          placeholderTextColor={colorScheme === 'dark' ? '#8e8e93' : '#8e8e93'}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />

        <ScrollView style={styles.productList}>
          {filteredProducts.map((product) => (
            <TouchableOpacity
              key={product.id}
              style={[styles.productSearchItem, { backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff' }]}
              onPress={() => {
                const style = LAYOUT_TEMPLATES[layoutTemplate]?.productStyle || LAYOUT_TEMPLATES.classic.productStyle;
                onSelectProduct({
                  ...product,
                  layout: style.layout,
                  showImage: style.showImage,
                  showDescription: style.showDescription,
                  showVolume: style.showVolume,
                  imageSize: style.imageSize,
                  fontSize: style.fontSize,
                  padding: style.padding,
                  imageUrl: product.imageUrls?.[0],
                  price: product.prices?.[0] ? `${product.prices[0].toFixed(2)} \u20ac` : '',
                  volume: product.volumes?.[0] || '',
                  allergens: product.allergens?.[0] || [],
                });
                onClose();
              }}
            >
              {product.imageUrls?.[0] && (
                <Image
                  source={{ uri: getProductImageUrl(product.imageUrls[0]), cache: 'default' }}
                  style={styles.productSearchImage}
                />
              )}
              <View style={styles.productSearchInfo}>
                <Text style={[styles.productSearchTitle, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>
                  {i18n.t(product.title)}
                </Text>
                {product.subtitle && (
                  <Text style={styles.productSearchSubtitle}>{i18n.t(product.subtitle)}</Text>
                )}
                <Text style={styles.productSearchPrice}>
                  {product.prices?.[0] ? `${product.prices[0].toFixed(2)} \u20ac` : 'N/A'}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

// Text Style Editor Modal
const TextStyleEditorModal = ({ visible, onClose, element, onUpdate }) => {
  const [text, setText] = useState(element?.data?.text || '');
  const [fontSize, setFontSize] = useState(element?.style?.fontSize || 16);
  const [fontWeight, setFontWeight] = useState(element?.style?.fontWeight || 'normal');
  const [color, setColor] = useState(element?.style?.color || '#000000');
  const [backgroundColor, setBackgroundColor] = useState(element?.style?.backgroundColor || 'transparent');
  const [textAlign, setTextAlign] = useState(element?.style?.textAlign || 'left');
  const [padding, setPadding] = useState(element?.style?.padding || 0);
  const [borderRadius, setBorderRadius] = useState(element?.style?.borderRadius || 0);
  const colorScheme = useColorScheme();

  useEffect(() => {
    if (element) {
      setText(element.data?.text || '');
      setFontSize(element.style?.fontSize || 16);
      setFontWeight(element.style?.fontWeight || 'normal');
      setColor(element.style?.color || '#000000');
      setBackgroundColor(element.style?.backgroundColor || 'transparent');
      setTextAlign(element.style?.textAlign || 'left');
      setPadding(element.style?.padding || 0);
      setBorderRadius(element.style?.borderRadius || 0);
    }
  }, [element]);

  const colorOptions = ['#000000', '#333333', '#666666', '#ffffff', '#C00000', '#006400', '#00008B', '#8B4513'];
  const bgColorOptions = ['transparent', '#ffffff', '#f5f5f5', '#000000', '#1a1a1a', '#fffbeb', '#f0fdf4', '#eff6ff', '#fef2f2', '#fdf4ff'];
  const fontSizes = [12, 14, 16, 18, 20, 24, 28, 32, 40, 48];
  const paddingOptions = [0, 4, 8, 12, 16, 20, 24];
  const borderRadiusOptions = [0, 4, 8, 12, 16, 24];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={[styles.modalContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7' }]}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Edit Text</Text>
          <TouchableOpacity onPress={onClose}>
            <MaterialIcons name="close" size={28} color={colorScheme === 'dark' ? '#fff' : '#000'} />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.editorContent}>
          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Text Content</Text>
          <TextInput
            style={[styles.textEditInput, {
              backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff',
              color: colorScheme === 'dark' ? '#fff' : '#000'
            }]}
            value={text}
            onChangeText={setText}
            multiline
            placeholder="Enter text..."
            placeholderTextColor="#8e8e93"
          />

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Font Size</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.optionsRow}>
            {fontSizes.map((size) => (
              <TouchableOpacity
                key={size}
                style={[styles.optionButton, fontSize === size && styles.optionButtonActive]}
                onPress={() => setFontSize(size)}
              >
                <Text style={[styles.optionButtonText, fontSize === size && styles.optionButtonTextActive]}>
                  {size}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Font Weight</Text>
          <View style={styles.optionsRow}>
            {['normal', 'bold', '600'].map((weight) => (
              <TouchableOpacity
                key={weight}
                style={[styles.optionButton, fontWeight === weight && styles.optionButtonActive]}
                onPress={() => setFontWeight(weight)}
              >
                <Text style={[styles.optionButtonText, fontWeight === weight && styles.optionButtonTextActive, { fontWeight: weight }]}>
                  {weight === '600' ? 'Semi' : weight.charAt(0).toUpperCase() + weight.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Text Color</Text>
          <View style={styles.colorOptionsRow}>
            {colorOptions.map((c) => (
              <TouchableOpacity
                key={c}
                style={[
                  styles.colorOption,
                  { backgroundColor: c },
                  color === c && styles.colorOptionActive,
                ]}
                onPress={() => setColor(c)}
              />
            ))}
          </View>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Text Alignment</Text>
          <View style={styles.optionsRow}>
            {['left', 'center', 'right'].map((align) => (
              <TouchableOpacity
                key={align}
                style={[styles.optionButton, textAlign === align && styles.optionButtonActive]}
                onPress={() => setTextAlign(align)}
              >
                <MaterialIcons
                  name={`format-align-${align}`}
                  size={20}
                  color={textAlign === align ? '#fff' : '#007AFF'}
                />
              </TouchableOpacity>
            ))}
          </View>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Background Color</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.colorOptionsRow}>
            {bgColorOptions.map((c) => (
              <TouchableOpacity
                key={c}
                style={[
                  styles.colorOption,
                  { backgroundColor: c === 'transparent' ? '#fff' : c, borderWidth: c === 'transparent' ? 1 : 0, borderColor: '#ccc' },
                  backgroundColor === c && styles.colorOptionActive,
                ]}
                onPress={() => setBackgroundColor(c)}
              >
                {c === 'transparent' && (
                  <MaterialIcons name="block" size={16} color="#ccc" />
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Padding</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.optionsRow}>
            {paddingOptions.map((p) => (
              <TouchableOpacity
                key={p}
                style={[styles.optionButton, padding === p && styles.optionButtonActive]}
                onPress={() => setPadding(p)}
              >
                <Text style={[styles.optionButtonText, padding === p && styles.optionButtonTextActive]}>
                  {p}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Border Radius</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.optionsRow}>
            {borderRadiusOptions.map((r) => (
              <TouchableOpacity
                key={r}
                style={[styles.optionButton, borderRadius === r && styles.optionButtonActive]}
                onPress={() => setBorderRadius(r)}
              >
                <Text style={[styles.optionButtonText, borderRadius === r && styles.optionButtonTextActive]}>
                  {r}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Quick Styles</Text>
          <View style={styles.optionsRow}>
            {Object.entries(TEXT_STYLES).map(([key, style]) => (
              <TouchableOpacity
                key={key}
                style={styles.quickStyleButton}
                onPress={() => {
                  setFontSize(style.fontSize);
                  setFontWeight(style.fontWeight);
                  setColor(style.color);
                }}
              >
                <Text style={[styles.quickStyleText, style]}>{key}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <TouchableOpacity
            style={styles.saveButton}
            onPress={() => {
              if (element) {
                onUpdate(element.id, {
                  data: { ...element.data, text },
                  style: { ...element.style, fontSize, fontWeight, color, textAlign, backgroundColor, padding, borderRadius },
                });
              }
              onClose();
            }}
          >
            <Text style={styles.saveButtonText}>Save Changes</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

// Allergen Legend Style Editor Modal
const AllergenStyleEditorModal = ({ visible, onClose, element, onUpdate }) => {
  const colorScheme = useColorScheme();
  const data = element?.data || {};
  const legendStyle = data.legendStyle || {};

  const [showTitle, setShowTitle] = useState(legendStyle.showTitle !== false);
  const [showBorder, setShowBorder] = useState(legendStyle.showBorder !== false);
  const [circleSize, setCircleSize] = useState(legendStyle.circleSize || 14);
  const [titleFontSize, setTitleFontSize] = useState(legendStyle.titleStyle?.fontSize || 14);
  const [titleFontWeight, setTitleFontWeight] = useState(legendStyle.titleStyle?.fontWeight || '600');
  const [titleColor, setTitleColor] = useState(legendStyle.titleStyle?.color || '#000000');
  const [labelFontSize, setLabelFontSize] = useState(legendStyle.labelStyle?.fontSize || 12);
  const [labelColor, setLabelColor] = useState(legendStyle.labelStyle?.color || '#000000');
  const [backgroundColor, setBackgroundColor] = useState(legendStyle.backgroundColor || 'transparent');

  useEffect(() => {
    if (visible && element) {
      const ls = element.data?.legendStyle || {};
      setShowTitle(ls.showTitle !== false);
      setShowBorder(ls.showBorder !== false);
      setCircleSize(ls.circleSize || 14);
      setTitleFontSize(ls.titleStyle?.fontSize || 14);
      setTitleFontWeight(ls.titleStyle?.fontWeight || '600');
      setTitleColor(ls.titleStyle?.color || '#000000');
      setLabelFontSize(ls.labelStyle?.fontSize || 12);
      setLabelColor(ls.labelStyle?.color || '#000000');
      setBackgroundColor(ls.backgroundColor || 'transparent');
    }
  }, [visible, element]);

  const colorOptions = ['#000000', '#333333', '#666666', '#ffffff', '#C00000', '#006400', '#00008B', '#8B4513'];
  const bgColorOptions = ['transparent', '#ffffff', '#f5f5f5', '#000000', '#1a1a1a', '#fffbeb', '#f0fdf4', '#eff6ff'];
  const circleSizes = [6, 8, 10, 12, 14, 16, 18, 20, 24];
  const fontSizes = [8, 9, 10, 11, 12, 13, 14, 16, 18, 20];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={[styles.modalContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7' }]}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Allergen Legend Style</Text>
          <TouchableOpacity onPress={onClose}>
            <MaterialIcons name="close" size={28} color={colorScheme === 'dark' ? '#fff' : '#000'} />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.editorContent}>
          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Display Options</Text>
          <View style={styles.optionsRow}>
            <TouchableOpacity
              style={[styles.optionButton, showTitle && styles.optionButtonActive]}
              onPress={() => setShowTitle(!showTitle)}
            >
              <Text style={[styles.optionButtonText, showTitle && styles.optionButtonTextActive]}>
                Show Title
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.optionButton, showBorder && styles.optionButtonActive]}
              onPress={() => setShowBorder(!showBorder)}
            >
              <Text style={[styles.optionButtonText, showBorder && styles.optionButtonTextActive]}>
                Show Border
              </Text>
            </TouchableOpacity>
          </View>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Circle Size</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.optionsRow}>
            {circleSizes.map((size) => (
              <TouchableOpacity
                key={size}
                style={[styles.optionButton, circleSize === size && styles.optionButtonActive]}
                onPress={() => setCircleSize(size)}
              >
                <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: '#007AFF' }} />
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Title Font Size</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.optionsRow}>
            {fontSizes.map((size) => (
              <TouchableOpacity
                key={size}
                style={[styles.optionButton, titleFontSize === size && styles.optionButtonActive]}
                onPress={() => setTitleFontSize(size)}
              >
                <Text style={[styles.optionButtonText, titleFontSize === size && styles.optionButtonTextActive]}>
                  {size}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Title Font Weight</Text>
          <View style={styles.optionsRow}>
            {['normal', '600', 'bold'].map((weight) => (
              <TouchableOpacity
                key={weight}
                style={[styles.optionButton, titleFontWeight === weight && styles.optionButtonActive]}
                onPress={() => setTitleFontWeight(weight)}
              >
                <Text style={[styles.optionButtonText, { fontWeight: weight }, titleFontWeight === weight && styles.optionButtonTextActive]}>
                  {weight === '600' ? 'Semi' : weight.charAt(0).toUpperCase() + weight.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Title Color</Text>
          <View style={styles.colorOptionsRow}>
            {colorOptions.map((c) => (
              <TouchableOpacity
                key={c}
                style={[
                  styles.colorOption,
                  { backgroundColor: c },
                  titleColor === c && styles.colorOptionSelected,
                ]}
                onPress={() => setTitleColor(c)}
              />
            ))}
          </View>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Label Font Size</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.optionsRow}>
            {fontSizes.map((size) => (
              <TouchableOpacity
                key={size}
                style={[styles.optionButton, labelFontSize === size && styles.optionButtonActive]}
                onPress={() => setLabelFontSize(size)}
              >
                <Text style={[styles.optionButtonText, labelFontSize === size && styles.optionButtonTextActive]}>
                  {size}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Label Color</Text>
          <View style={styles.colorOptionsRow}>
            {colorOptions.map((c) => (
              <TouchableOpacity
                key={c}
                style={[
                  styles.colorOption,
                  { backgroundColor: c },
                  labelColor === c && styles.colorOptionSelected,
                ]}
                onPress={() => setLabelColor(c)}
              />
            ))}
          </View>

          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Background Color</Text>
          <View style={styles.colorOptionsRow}>
            {bgColorOptions.map((c) => (
              <TouchableOpacity
                key={c}
                style={[
                  styles.colorOption,
                  { backgroundColor: c === 'transparent' ? '#fff' : c },
                  c === 'transparent' && { borderStyle: 'dashed' },
                  backgroundColor === c && styles.colorOptionSelected,
                ]}
                onPress={() => setBackgroundColor(c)}
              >
                {c === 'transparent' && (
                  <MaterialIcons name="block" size={16} color="#ccc" />
                )}
              </TouchableOpacity>
            ))}
          </View>

          <TouchableOpacity
            style={styles.saveButton}
            onPress={() => {
              if (element) {
                onUpdate(element.id, {
                  data: {
                    ...element.data,
                    legendStyle: {
                      showTitle,
                      showBorder,
                      circleSize,
                      titleStyle: { fontSize: titleFontSize, fontWeight: titleFontWeight, color: titleColor },
                      labelStyle: { fontSize: labelFontSize, color: labelColor },
                      backgroundColor,
                    },
                  },
                });
              }
              onClose();
            }}
          >
            <Text style={styles.saveButtonText}>Save Changes</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

// Product Style Editor Modal
const ProductStyleEditorModal = ({ visible, onClose, productStyles, onUpdateStyles, productScale, onUpdateScale, borderStyles, onUpdateBorderStyles }) => {
  const colorScheme = useColorScheme();
  const [activeTab, setActiveTab] = useState('title');
  const [localScale, setLocalScale] = useState(productScale || 1.0);

  // Local state for each style type
  const [titleStyle, setTitleStyle] = useState(productStyles?.title || DEFAULT_PRODUCT_STYLES.title);
  const [descriptionStyle, setDescriptionStyle] = useState(productStyles?.description || DEFAULT_PRODUCT_STYLES.description);
  const [variantStyle, setVariantStyle] = useState(productStyles?.variant || DEFAULT_PRODUCT_STYLES.variant);
  const [priceStyle, setPriceStyle] = useState(productStyles?.price || DEFAULT_PRODUCT_STYLES.price);
  const [backgroundStyle, setBackgroundStyle] = useState(productStyles?.background || DEFAULT_PRODUCT_STYLES.background);
  const [showAllergens, setShowAllergens] = useState(productStyles?.showAllergens !== false);

  // Local state for border styles
  const [localBorderStyles, setLocalBorderStyles] = useState(borderStyles || DEFAULT_BORDER_STYLES);

  useEffect(() => {
    if (productStyles) {
      setTitleStyle(productStyles.title || DEFAULT_PRODUCT_STYLES.title);
      setDescriptionStyle(productStyles.description || DEFAULT_PRODUCT_STYLES.description);
      setVariantStyle(productStyles.variant || DEFAULT_PRODUCT_STYLES.variant);
      setPriceStyle(productStyles.price || DEFAULT_PRODUCT_STYLES.price);
      setBackgroundStyle(productStyles.background || DEFAULT_PRODUCT_STYLES.background);
      setShowAllergens(productStyles.showAllergens !== false);
    }
    setLocalScale(productScale || 1.0);
    setLocalBorderStyles(borderStyles || DEFAULT_BORDER_STYLES);
  }, [productStyles, productScale, borderStyles]);

  const colorOptions = ['#000000', '#333333', '#666666', '#888888', '#ffffff', '#C00000', '#006400', '#00008B', '#8B4513', '#D4AF37'];
  const fontSizes = [10, 12, 14, 16, 18, 20, 24, 28, 32];
  const fontWeights = ['normal', '500', '600', 'bold'];

  const tabs = [
    { id: 'title', name: 'Title', icon: 'title' },
    { id: 'description', name: 'Description', icon: 'description' },
    { id: 'variant', name: 'Variant', icon: 'local-offer' },
    { id: 'price', name: 'Price', icon: 'attach-money' },
    { id: 'background', name: 'Background', icon: 'format-color-fill' },
  ];

  const getCurrentStyle = () => {
    switch (activeTab) {
      case 'title': return titleStyle;
      case 'description': return descriptionStyle;
      case 'variant': return variantStyle;
      case 'price': return priceStyle;
      case 'background': return backgroundStyle;
      default: return titleStyle;
    }
  };

  const setCurrentStyle = (newStyle) => {
    switch (activeTab) {
      case 'title': setTitleStyle(newStyle); break;
      case 'description': setDescriptionStyle(newStyle); break;
      case 'variant': setVariantStyle(newStyle); break;
      case 'price': setPriceStyle(newStyle); break;
      case 'background': setBackgroundStyle(newStyle); break;
    }
  };

  const currentStyle = getCurrentStyle();

  const backgroundColorOptions = [
    'transparent', '#ffffff', '#f5f5f5', '#eeeeee', '#e0e0e0',
    '#fff8e1', '#fff3e0', '#fce4ec', '#e8f5e9', '#e3f2fd',
    '#000000', '#333333', '#1a1a2e', '#2d2d44', '#3d3d5c',
  ];

  const StyleEditor = () => {
    // Background tab has different UI
    if (activeTab === 'background') {
      return (
        <View style={{ backgroundColor: 'transparent' }}>
          {/* Preview */}
          <View style={[styles.stylePreviewBox, { backgroundColor: backgroundStyle.color === 'transparent' ? (colorScheme === 'dark' ? '#2c2c2e' : '#ffffff') : backgroundStyle.color }]}>
            <Text style={{ fontSize: 12, color: backgroundStyle.color === 'transparent' || backgroundStyle.color === '#ffffff' || backgroundStyle.color?.startsWith('#f') || backgroundStyle.color?.startsWith('#e') ? '#666' : '#fff', marginBottom: 8 }}>Preview:</Text>
            <Text style={{ fontSize: 16, fontWeight: '600', color: backgroundStyle.color === 'transparent' || backgroundStyle.color === '#ffffff' || backgroundStyle.color?.startsWith('#f') || backgroundStyle.color?.startsWith('#e') ? '#000' : '#fff' }}>
              Product Title
            </Text>
            <Text style={{ fontSize: 14, color: backgroundStyle.color === 'transparent' || backgroundStyle.color === '#ffffff' || backgroundStyle.color?.startsWith('#f') || backgroundStyle.color?.startsWith('#e') ? '#666' : '#ddd' }}>
              12.99 €
            </Text>
          </View>

          {/* Background Color */}
          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Background Color</Text>
          <View style={styles.colorOptionsRow}>
            {backgroundColorOptions.map((c) => (
              <TouchableOpacity
                key={c}
                style={[
                  styles.colorOption,
                  {
                    backgroundColor: c === 'transparent' ? '#fff' : c,
                    borderWidth: c === 'transparent' ? 2 : 1,
                    borderStyle: c === 'transparent' ? 'dashed' : 'solid',
                  },
                  backgroundStyle.color === c && styles.colorOptionActive,
                ]}
                onPress={() => setBackgroundStyle({ color: c })}
              >
                {c === 'transparent' && (
                  <MaterialIcons name="block" size={16} color="#999" />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </View>
      );
    }

    return (
      <View style={{ backgroundColor: 'transparent' }}>
        {/* Preview */}
        <View style={[styles.stylePreviewBox, { backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff' }]}>
          <Text style={{ fontSize: 12, color: '#8e8e93', marginBottom: 8 }}>Preview:</Text>
          <Text style={{
            fontSize: currentStyle.fontSize,
            fontWeight: currentStyle.fontWeight,
            color: currentStyle.color,
          }}>
            {activeTab === 'title' ? 'Product Title' :
             activeTab === 'description' ? 'Product description text' :
             activeTab === 'variant' ? '500ml' : '12.99 €'}
          </Text>
        </View>

        {/* Font Size */}
        <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Font Size</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.optionsRow}>
          {fontSizes.map((size) => (
            <TouchableOpacity
              key={size}
              style={[styles.optionButton, currentStyle.fontSize === size && styles.optionButtonActive]}
              onPress={() => setCurrentStyle({ ...currentStyle, fontSize: size })}
            >
              <Text style={[styles.optionButtonText, currentStyle.fontSize === size && styles.optionButtonTextActive]}>
                {size}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Font Weight */}
        <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Font Weight</Text>
        <View style={styles.optionsRow}>
          {fontWeights.map((weight) => (
            <TouchableOpacity
              key={weight}
              style={[styles.optionButton, currentStyle.fontWeight === weight && styles.optionButtonActive]}
              onPress={() => setCurrentStyle({ ...currentStyle, fontWeight: weight })}
            >
              <Text style={[
                styles.optionButtonText,
                currentStyle.fontWeight === weight && styles.optionButtonTextActive,
                { fontWeight: weight }
              ]}>
                {weight === '500' ? 'Medium' : weight === '600' ? 'Semi' : weight.charAt(0).toUpperCase() + weight.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Color */}
        <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>Color</Text>
        <View style={styles.colorOptionsRow}>
          {colorOptions.map((c) => (
            <TouchableOpacity
              key={c}
              style={[
                styles.colorOption,
                { backgroundColor: c },
                currentStyle.color === c && styles.colorOptionActive,
              ]}
              onPress={() => setCurrentStyle({ ...currentStyle, color: c })}
            />
          ))}
        </View>
      </View>
    );
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={[styles.modalContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7' }]}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Product Text Styles</Text>
          <TouchableOpacity onPress={onClose}>
            <MaterialIcons name="close" size={28} color={colorScheme === 'dark' ? '#fff' : '#000'} />
          </TouchableOpacity>
        </View>

        {/* Style Type Tabs */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.categoryTabs}
          contentContainerStyle={{ paddingHorizontal: 12 }}
        >
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.categoryTab,
                activeTab === tab.id && styles.categoryTabActive,
              ]}
              onPress={() => setActiveTab(tab.id)}
            >
              <MaterialIcons
                name={tab.icon}
                size={18}
                color={activeTab === tab.id ? '#fff' : '#007AFF'}
              />
              <Text
                style={[
                  styles.categoryTabText,
                  activeTab === tab.id && styles.categoryTabTextActive,
                ]}
              >
                {tab.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        <ScrollView style={styles.editorContent}>
          <StyleEditor />

          {/* Global Scale */}
          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000', marginTop: 24 }]}>Global Product Scale</Text>
          <Text style={{ color: '#8e8e93', fontSize: 12, marginBottom: 8, marginHorizontal: 16 }}>
            Scale all product elements (image, text, spacing)
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.optionsRow}>
            {[0.1, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6, 0.75, 0.8, 0.9, 1.0, 1.1, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0].map((scale) => (
              <TouchableOpacity
                key={scale}
                style={[styles.optionButton, localScale === scale && styles.optionButtonActive]}
                onPress={() => setLocalScale(scale)}
              >
                <Text style={[styles.optionButtonText, localScale === scale && styles.optionButtonTextActive]}>
                  {scale === 1.0 ? '1x' : `${scale}x`}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Show Allergens Toggle */}
          <View style={{ marginTop: 24, marginHorizontal: 16 }}>
            <TouchableOpacity
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff',
                padding: 16,
                borderRadius: 12,
              }}
              onPress={() => setShowAllergens(!showAllergens)}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                <MaterialIcons name="warning" size={24} color={showAllergens ? '#007AFF' : '#8e8e93'} />
                <View>
                  <Text style={{ fontSize: 16, fontWeight: '500', color: colorScheme === 'dark' ? '#fff' : '#000' }}>
                    Show Allergens
                  </Text>
                  <Text style={{ fontSize: 12, color: '#8e8e93' }}>
                    Display allergen badges on products
                  </Text>
                </View>
              </View>
              <View style={{
                width: 51,
                height: 31,
                borderRadius: 16,
                backgroundColor: showAllergens ? '#34C759' : (colorScheme === 'dark' ? '#39393D' : '#e9e9eb'),
                justifyContent: 'center',
                paddingHorizontal: 2,
              }}>
                <View style={{
                  width: 27,
                  height: 27,
                  borderRadius: 14,
                  backgroundColor: '#fff',
                  alignSelf: showAllergens ? 'flex-end' : 'flex-start',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.2,
                  shadowRadius: 2,
                }} />
              </View>
            </TouchableOpacity>
          </View>

          {/* Border Styles Section */}
          <View style={{ marginTop: 24, marginHorizontal: 16, padding: 16, backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff', borderRadius: 12 }}>
            <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000', marginLeft: 0, marginTop: 0 }]}>Element Borders</Text>

            {/* Border Preview */}
            <View style={[styles.stylePreviewBox, {
              backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7',
              borderWidth: localBorderStyles.width,
              borderColor: localBorderStyles.color,
              borderStyle: localBorderStyles.style === 'none' ? 'solid' : localBorderStyles.style,
              borderRadius: localBorderStyles.radius,
              marginBottom: 16,
            }]}>
              <Text style={{ fontSize: 12, color: '#8e8e93', marginBottom: 4 }}>Preview:</Text>
              <Text style={{ fontSize: 16, color: colorScheme === 'dark' ? '#fff' : '#000' }}>Sample Element</Text>
            </View>

            {/* Border Style */}
            <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000', marginLeft: 0 }]}>Border Style</Text>
            <View style={[styles.optionsRow, { marginHorizontal: 0 }]}>
              {BORDER_STYLE_OPTIONS.map((style) => (
                <TouchableOpacity
                  key={style}
                  style={[styles.optionButton, localBorderStyles.style === style && styles.optionButtonActive]}
                  onPress={() => setLocalBorderStyles({ ...localBorderStyles, style, width: style === 'none' ? 0 : (localBorderStyles.width || 1) })}
                >
                  <Text style={[styles.optionButtonText, localBorderStyles.style === style && styles.optionButtonTextActive]}>
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Border Width */}
            <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000', marginLeft: 0 }]}>Border Width</Text>
            <View style={[styles.optionsRow, { marginHorizontal: 0 }]}>
              {BORDER_WIDTH_OPTIONS.map((width) => (
                <TouchableOpacity
                  key={width}
                  style={[styles.optionButton, localBorderStyles.width === width && styles.optionButtonActive]}
                  onPress={() => setLocalBorderStyles({ ...localBorderStyles, width, style: width === 0 ? 'none' : (localBorderStyles.style === 'none' ? 'solid' : localBorderStyles.style) })}
                >
                  <Text style={[styles.optionButtonText, localBorderStyles.width === width && styles.optionButtonTextActive]}>
                    {width === 0 ? 'None' : `${width}px`}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Border Color */}
            <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000', marginLeft: 0 }]}>Border Color</Text>
            <View style={[styles.colorOptionsRow, { marginHorizontal: 0 }]}>
              {colorOptions.map((c) => (
                <TouchableOpacity
                  key={c}
                  style={[
                    styles.colorOption,
                    { backgroundColor: c },
                    localBorderStyles.color === c && styles.colorOptionActive,
                  ]}
                  onPress={() => setLocalBorderStyles({ ...localBorderStyles, color: c })}
                />
              ))}
            </View>

            {/* Border Radius */}
            <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000', marginLeft: 0 }]}>Border Radius</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={[styles.optionsRow, { marginHorizontal: 0 }]}>
              {BORDER_RADIUS_OPTIONS.map((radius) => (
                <TouchableOpacity
                  key={radius}
                  style={[styles.optionButton, localBorderStyles.radius === radius && styles.optionButtonActive]}
                  onPress={() => setLocalBorderStyles({ ...localBorderStyles, radius })}
                >
                  <Text style={[styles.optionButtonText, localBorderStyles.radius === radius && styles.optionButtonTextActive]}>
                    {radius === 0 ? 'Sharp' : `${radius}px`}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {/* Reset to Defaults */}
          <TouchableOpacity
            style={[styles.resetButton, { marginTop: 20 }]}
            onPress={() => {
              setTitleStyle(DEFAULT_PRODUCT_STYLES.title);
              setDescriptionStyle(DEFAULT_PRODUCT_STYLES.description);
              setVariantStyle(DEFAULT_PRODUCT_STYLES.variant);
              setPriceStyle(DEFAULT_PRODUCT_STYLES.price);
              setLocalScale(1.0);
              setLocalBorderStyles(DEFAULT_BORDER_STYLES);
            }}
          >
            <MaterialIcons name="refresh" size={20} color="#FF9500" />
            <Text style={styles.resetButtonText}>Reset All to Defaults</Text>
          </TouchableOpacity>

          {/* Save Button */}
          <TouchableOpacity
            style={styles.saveButton}
            onPress={() => {
              onUpdateStyles({
                title: titleStyle,
                description: descriptionStyle,
                variant: variantStyle,
                price: priceStyle,
                background: backgroundStyle,
                showAllergens,
              });
              if (onUpdateScale) {
                onUpdateScale(localScale);
              }
              if (onUpdateBorderStyles) {
                onUpdateBorderStyles(localBorderStyles);
              }
              onClose();
            }}
          >
            <Text style={styles.saveButtonText}>Apply Styles</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

// Element Style Editor Modal - for per-element styling
const ElementStyleEditorModal = ({ visible, onClose, element, onUpdate, globalStyles }) => {
  const colorScheme = useColorScheme();
  const [bgColor, setBgColor] = useState(element?.data?.elementStyles?.background?.color || 'inherit');

  useEffect(() => {
    if (element?.data?.elementStyles?.background?.color) {
      setBgColor(element.data.elementStyles.background.color);
    } else {
      setBgColor('inherit');
    }
  }, [element]);

  const backgroundColorOptions = [
    'inherit', 'transparent', '#ffffff', '#f5f5f5', '#eeeeee', '#e0e0e0',
    '#fff8e1', '#fff3e0', '#fce4ec', '#e8f5e9', '#e3f2fd',
    '#000000', '#333333', '#1a1a2e', '#2d2d44', '#3d3d5c',
  ];

  const handleSave = () => {
    if (!element) return;

    const newData = {
      ...element.data,
      elementStyles: {
        ...element.data?.elementStyles,
        background: { color: bgColor },
      },
    };

    onUpdate(element.id, { data: newData });
    onClose();
  };

  const getPreviewBgColor = () => {
    if (bgColor === 'inherit') {
      return globalStyles?.background?.color || 'transparent';
    }
    return bgColor;
  };

  if (!element) return null;

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={[styles.modalContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7' }]}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Element Style</Text>
          <TouchableOpacity onPress={onClose}>
            <MaterialIcons name="close" size={28} color={colorScheme === 'dark' ? '#fff' : '#000'} />
          </TouchableOpacity>
        </View>

        <ScrollView style={{ flex: 1, padding: 16 }}>
          {/* Preview */}
          <View style={[styles.stylePreviewBox, { backgroundColor: getPreviewBgColor() === 'transparent' ? (colorScheme === 'dark' ? '#2c2c2e' : '#fff') : getPreviewBgColor() }]}>
            <Text style={{ fontSize: 12, color: getPreviewBgColor() === 'transparent' || getPreviewBgColor()?.startsWith('#f') || getPreviewBgColor()?.startsWith('#e') || getPreviewBgColor() === '#ffffff' ? '#666' : '#fff', marginBottom: 8 }}>Preview:</Text>
            <Text style={{ fontSize: 16, fontWeight: '600', color: getPreviewBgColor() === 'transparent' || getPreviewBgColor()?.startsWith('#f') || getPreviewBgColor()?.startsWith('#e') || getPreviewBgColor() === '#ffffff' ? '#000' : '#fff' }}>
              {element.data?.title || 'Product Title'}
            </Text>
            <Text style={{ fontSize: 14, color: getPreviewBgColor() === 'transparent' || getPreviewBgColor()?.startsWith('#f') || getPreviewBgColor()?.startsWith('#e') || getPreviewBgColor() === '#ffffff' ? '#666' : '#ddd' }}>
              {element.data?.price || '12.99 €'}
            </Text>
          </View>

          {/* Background Color */}
          <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000', marginTop: 16 }]}>Background Color</Text>
          <Text style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Use "Inherit" to use the global product style</Text>
          <View style={styles.colorOptionsRow}>
            {backgroundColorOptions.map((c) => (
              <TouchableOpacity
                key={c}
                style={[
                  styles.colorOption,
                  {
                    backgroundColor: c === 'inherit' ? '#007AFF' : c === 'transparent' ? '#fff' : c,
                    borderWidth: c === 'transparent' ? 2 : 1,
                    borderStyle: c === 'transparent' ? 'dashed' : 'solid',
                  },
                  bgColor === c && styles.colorOptionActive,
                ]}
                onPress={() => setBgColor(c)}
              >
                {c === 'inherit' && (
                  <Text style={{ color: '#fff', fontSize: 8, fontWeight: 'bold' }}>G</Text>
                )}
                {c === 'transparent' && (
                  <MaterialIcons name="block" size={16} color="#999" />
                )}
              </TouchableOpacity>
            ))}
          </View>

          {/* Save Button */}
          <TouchableOpacity style={[styles.saveButton, { marginTop: 24 }]} onPress={handleSave}>
            <Text style={styles.saveButtonText}>Apply Style</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

// Background Selector Modal
const BackgroundSelectorModal = ({ visible, onClose, onSelectBackground, currentBackground }) => {
  const colorScheme = useColorScheme();
  const [customColor, setCustomColor] = useState('#FFFFFF');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All', icon: 'apps' },
    { id: 'solid', name: 'Solid', icon: 'square' },
    { id: 'texture', name: 'Textures', icon: 'texture' },
    { id: 'pattern', name: 'Patterns', icon: 'grid-on' },
    { id: 'gradient', name: 'Gradients', icon: 'gradient' },
    { id: 'artistic', name: 'Artistic', icon: 'palette' },
  ];

  const filteredBackgrounds = selectedCategory === 'all'
    ? AI_BACKGROUNDS
    : AI_BACKGROUNDS.filter(bg => bg.category === selectedCategory);

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.8,
    });

    if (!result.canceled) {
      onSelectBackground({ type: 'image', uri: result.assets[0].uri });
      onClose();
    }
  };

  const isLightColor = (color) => {
    if (!color) return true;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128;
  };

  // Mini pattern preview renderer
  const renderMiniPattern = (bg) => {
    const patternColor = bg.patternColor || bg.secondaryColor || '#CCCCCC';
    const baseStyle = { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' };

    switch (bg.pattern) {
      case 'dots':
        return (
          <View style={[baseStyle, { flexDirection: 'column', justifyContent: 'space-evenly', padding: 4 }]}>
            {[0, 1, 2, 3, 4, 5].map(row => (
              <View key={row} style={{ flexDirection: 'row', flex: 1, justifyContent: 'space-evenly', alignItems: 'center' }}>
                {[0, 1, 2, 3, 4, 5].map(col => (
                  <View key={col} style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: patternColor }} />
                ))}
              </View>
            ))}
          </View>
        );
      case 'stripes':
        return (
          <View style={[baseStyle, { flexDirection: 'column' }]}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
              <View key={i} style={{ flex: 1, backgroundColor: i % 2 === 0 ? 'transparent' : patternColor }} />
            ))}
          </View>
        );
      case 'grid':
        return (
          <View style={[baseStyle, { flexDirection: 'column', padding: 2 }]}>
            {[0, 1, 2, 3, 4].map(row => (
              <View key={row} style={{ flexDirection: 'row', flex: 1 }}>
                {[0, 1, 2, 3, 4].map(col => (
                  <View key={col} style={{ flex: 1, borderWidth: 1, borderColor: patternColor }} />
                ))}
              </View>
            ))}
          </View>
        );
      case 'diagonal':
        return (
          <View style={baseStyle}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
              <View key={i} style={{ position: 'absolute', width: '150%', height: 2, backgroundColor: patternColor, top: `${i * 10}%`, left: '-25%', transform: [{ rotate: '-45deg' }] }} />
            ))}
          </View>
        );
      case 'wood':
        return (
          <View style={[baseStyle, { flexDirection: 'column' }]}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <View key={i} style={{ flex: 1, backgroundColor: i % 3 === 0 ? bg.secondaryColor : bg.baseColor }} />
            ))}
          </View>
        );
      case 'marble':
        return (
          <View style={baseStyle}>
            <View style={{ position: 'absolute', top: '15%', left: '5%', width: '70%', height: 2, backgroundColor: bg.secondaryColor, transform: [{ rotate: '25deg' }], opacity: 0.6 }} />
            <View style={{ position: 'absolute', top: '40%', left: '20%', width: '60%', height: 2, backgroundColor: bg.accentColor || bg.secondaryColor, transform: [{ rotate: '-15deg' }], opacity: 0.5 }} />
            <View style={{ position: 'absolute', top: '65%', left: '0%', width: '55%', height: 2, backgroundColor: bg.secondaryColor, transform: [{ rotate: '20deg' }], opacity: 0.4 }} />
            <View style={{ position: 'absolute', top: '85%', left: '30%', width: '50%', height: 1.5, backgroundColor: bg.secondaryColor, transform: [{ rotate: '-10deg' }], opacity: 0.3 }} />
          </View>
        );
      case 'chalk':
      case 'paper':
        return (
          <View style={baseStyle}>
            {[[10, 15], [25, 40], [50, 20], [15, 55], [60, 45], [35, 10], [45, 60], [70, 30], [80, 50], [30, 70], [65, 15], [20, 80], [75, 75], [5, 35], [90, 25], [55, 85]].map(([top, left], i) => (
              <View key={i} style={{ position: 'absolute', width: 3, height: 3, borderRadius: 1.5, backgroundColor: bg.pattern === 'chalk' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.15)', top: `${top}%`, left: `${left}%` }} />
            ))}
          </View>
        );
      case 'chevron':
        return (
          <View style={[baseStyle, { flexDirection: 'column', alignItems: 'center', justifyContent: 'space-evenly', padding: 4 }]}>
            {[0, 1, 2, 3, 4].map(i => (
              <View key={i} style={{ width: 0, height: 0, borderLeftWidth: 40, borderRightWidth: 40, borderBottomWidth: 10, borderLeftColor: 'transparent', borderRightColor: 'transparent', borderBottomColor: patternColor }} />
            ))}
          </View>
        );
      case 'hexagon':
      case 'diamonds':
        return (
          <View style={[baseStyle, { flexDirection: 'column' }]}>
            {[0, 1, 2, 3, 4].map(row => (
              <View key={row} style={{ flexDirection: 'row', flex: 1, marginLeft: row % 2 === 0 ? 0 : '10%' }}>
                {[0, 1, 2, 3, 4].map(col => (
                  <View key={col} style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                    <View style={{ width: 10, height: 10, backgroundColor: patternColor, transform: [{ rotate: '45deg' }] }} />
                  </View>
                ))}
              </View>
            ))}
          </View>
        );
      case 'waves':
        return (
          <View style={[baseStyle, { flexDirection: 'column', justifyContent: 'space-evenly' }]}>
            {[0, 1, 2, 3, 4, 5].map(i => (
              <View key={i} style={{ height: 3, marginHorizontal: 4, borderRadius: 50, backgroundColor: patternColor, opacity: 0.6 + (i % 2) * 0.2 }} />
            ))}
          </View>
        );
      case 'crosses':
        return (
          <View style={[baseStyle, { flexDirection: 'column' }]}>
            {[0, 1, 2, 3, 4].map(row => (
              <View key={row} style={{ flexDirection: 'row', flex: 1 }}>
                {[0, 1, 2, 3, 4].map(col => (
                  <View key={col} style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                    <View style={{ position: 'absolute', width: 8, height: 2, backgroundColor: patternColor }} />
                    <View style={{ position: 'absolute', width: 2, height: 8, backgroundColor: patternColor }} />
                  </View>
                ))}
              </View>
            ))}
          </View>
        );
      case 'linen':
      case 'canvas':
        return (
          <View style={[baseStyle, { flexDirection: 'column' }]}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map(row => (
              <View key={row} style={{ flexDirection: 'row', flex: 1 }}>
                {[0, 1, 2, 3, 4, 5, 6, 7].map(col => (
                  <View key={col} style={{ flex: 1, backgroundColor: (row + col) % 2 === 0 ? 'transparent' : bg.secondaryColor, opacity: 0.5 }} />
                ))}
              </View>
            ))}
          </View>
        );
      case 'concrete':
      case 'leather':
        return (
          <View style={baseStyle}>
            {[[10, 15, 12], [30, 60, 8], [25, 35, 10], [60, 20, 14], [50, 70, 9], [15, 80, 11], [75, 45, 10], [80, 80, 8], [45, 10, 7], [70, 5, 12]].map(([top, left, size], i) => (
              <View key={i} style={{ position: 'absolute', width: size, height: size, borderRadius: size / 2, backgroundColor: bg.secondaryColor, opacity: 0.3 + (i % 3) * 0.15, top: `${top}%`, left: `${left}%` }} />
            ))}
          </View>
        );
      case 'vintage':
      case 'artdeco':
        return (
          <View style={[baseStyle, { borderWidth: 3, borderColor: bg.patternColor || bg.borderColor || '#D4AF37' }]}>
            <View style={{ position: 'absolute', top: 8, left: 8, right: 8, bottom: 8, borderWidth: 1, borderColor: bg.patternColor || bg.borderColor || '#D4AF37' }} />
          </View>
        );
      case 'rustic':
        return (
          <View style={[baseStyle, { flexDirection: 'column' }]}>
            {[0, 1, 2, 3, 4, 5].map(i => (
              <View key={i} style={{ flex: 1, backgroundColor: i % 2 === 0 ? bg.baseColor : bg.secondaryColor, borderBottomWidth: 1, borderBottomColor: 'rgba(0,0,0,0.2)' }} />
            ))}
          </View>
        );
      case 'minimal':
        return (
          <View style={[baseStyle, { borderWidth: 2, borderColor: bg.borderColor || '#CCCCCC' }]} />
        );
      case 'neon':
        return (
          <View style={baseStyle}>
            <View style={{ position: 'absolute', top: '25%', left: '10%', right: '10%', height: 3, backgroundColor: bg.patternColor, shadowColor: bg.patternColor, shadowRadius: 6, shadowOpacity: 1 }} />
            <View style={{ position: 'absolute', top: '55%', left: '15%', right: '15%', height: 3, backgroundColor: bg.patternColor, shadowColor: bg.patternColor, shadowRadius: 6, shadowOpacity: 1 }} />
            <View style={{ position: 'absolute', top: '80%', left: '20%', right: '20%', height: 2, backgroundColor: bg.patternColor, shadowColor: bg.patternColor, shadowRadius: 4, shadowOpacity: 0.8 }} />
          </View>
        );
      case 'watercolor':
        return (
          <View style={baseStyle}>
            <View style={{ position: 'absolute', top: '10%', left: '5%', width: '50%', height: '40%', borderRadius: 30, backgroundColor: bg.secondaryColor, opacity: 0.5 }} />
            <View style={{ position: 'absolute', top: '35%', left: '35%', width: '45%', height: '45%', borderRadius: 25, backgroundColor: bg.accentColor || bg.secondaryColor, opacity: 0.4 }} />
            <View style={{ position: 'absolute', top: '60%', left: '15%', width: '35%', height: '30%', borderRadius: 20, backgroundColor: bg.secondaryColor, opacity: 0.3 }} />
          </View>
        );
      case 'brushed':
        return (
          <View style={[baseStyle, { flexDirection: 'column' }]}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
              <View key={i} style={{ flex: 1, backgroundColor: i % 2 === 0 ? 'transparent' : bg.secondaryColor, opacity: 0.5 + (i % 3) * 0.15 }} />
            ))}
          </View>
        );
      case 'torn':
        return (
          <View style={baseStyle}>
            <View style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '10%', backgroundColor: bg.secondaryColor }} />
            <View style={{ position: 'absolute', top: '10%', left: 0, width: '15%', height: '8%', backgroundColor: bg.secondaryColor }} />
            <View style={{ position: 'absolute', top: '10%', left: '25%', width: '20%', height: '6%', backgroundColor: bg.secondaryColor }} />
            <View style={{ position: 'absolute', top: '10%', left: '60%', width: '18%', height: '7%', backgroundColor: bg.secondaryColor }} />
            <View style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '10%', backgroundColor: bg.secondaryColor }} />
            <View style={{ position: 'absolute', bottom: '10%', left: '5%', width: '18%', height: '7%', backgroundColor: bg.secondaryColor }} />
            <View style={{ position: 'absolute', bottom: '10%', left: '40%', width: '15%', height: '6%', backgroundColor: bg.secondaryColor }} />
            <View style={{ position: 'absolute', bottom: '10%', left: '70%', width: '22%', height: '8%', backgroundColor: bg.secondaryColor }} />
          </View>
        );
      case 'newspaper':
        return (
          <View style={[baseStyle, { flexDirection: 'column', padding: 4 }]}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
              <View key={i} style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
                <View style={{ flex: 1, height: 2, backgroundColor: bg.secondaryColor, marginRight: 4, opacity: 0.6 }} />
                <View style={{ flex: 0.5 + (i % 3) * 0.2, height: 2, backgroundColor: bg.secondaryColor, opacity: 0.4 }} />
              </View>
            ))}
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={[styles.modalContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7' }]}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Backgrounds</Text>
          <TouchableOpacity onPress={onClose}>
            <MaterialIcons name="close" size={28} color={colorScheme === 'dark' ? '#fff' : '#000'} />
          </TouchableOpacity>
        </View>

        {/* Category Tabs */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.categoryTabs}
          contentContainerStyle={{ paddingHorizontal: 12 }}
        >
          {categories.map((cat) => (
            <TouchableOpacity
              key={cat.id}
              style={[
                styles.categoryTab,
                selectedCategory === cat.id && styles.categoryTabActive,
              ]}
              onPress={() => setSelectedCategory(cat.id)}
            >
              <MaterialIcons
                name={cat.icon}
                size={18}
                color={selectedCategory === cat.id ? '#fff' : '#007AFF'}
              />
              <Text
                style={[
                  styles.categoryTabText,
                  selectedCategory === cat.id && styles.categoryTabTextActive,
                ]}
              >
                {cat.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        <ScrollView style={styles.backgroundOptions}>
          <View style={styles.backgroundGrid}>
            {filteredBackgrounds.map((bg) => {
              const baseColor = bg.baseColor || (bg.gradientColors ? bg.gradientColors[0] : '#FFFFFF');
              const textColor = isLightColor(baseColor) ? '#000' : '#fff';

              return (
                <TouchableOpacity
                  key={bg.id}
                  style={[
                    styles.backgroundOption,
                    { backgroundColor: baseColor },
                    currentBackground?.id === bg.id && styles.backgroundOptionActive,
                  ]}
                  onPress={() => {
                    onSelectBackground(bg);
                    onClose();
                  }}
                >
                  {/* Gradient preview (render first so pattern overlays it if both exist) */}
                  {bg.gradientColors && (
                    <View style={styles.gradientPreview}>
                      {bg.gradientColors.map((color, idx) => (
                        <View
                          key={idx}
                          style={{
                            flex: 1,
                            backgroundColor: color,
                          }}
                        />
                      ))}
                    </View>
                  )}

                  {/* Render actual pattern preview */}
                  {bg.pattern && bg.pattern !== 'gradient-vertical' && renderMiniPattern(bg)}

                  {/* Text overlay */}
                  <View style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 6, backgroundColor: 'rgba(0,0,0,0.4)' }}>
                    <Text style={[styles.backgroundName, { color: '#fff', fontSize: 11 }]} numberOfLines={1}>
                      {bg.name}
                    </Text>
                  </View>

                  {/* Selected indicator */}
                  {currentBackground?.id === bg.id && (
                    <View style={{ position: 'absolute', top: 4, right: 4 }}>
                      <MaterialIcons name="check-circle" size={18} color="#007AFF" />
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>

          <Text style={[styles.sectionTitle, { color: colorScheme === 'dark' ? '#fff' : '#000', marginTop: 20 }]}>
            Custom Background
          </Text>

          <TouchableOpacity style={styles.uploadButton} onPress={pickImage}>
            <MaterialIcons name="add-photo-alternate" size={24} color="#007AFF" />
            <Text style={styles.uploadButtonText}>Upload Custom Image</Text>
          </TouchableOpacity>

          <View style={styles.customColorSection}>
            <Text style={[styles.editorLabel, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>
              Solid Color
            </Text>
            <TextInput
              style={[styles.colorInput, {
                backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff',
                color: colorScheme === 'dark' ? '#fff' : '#000'
              }]}
              value={customColor}
              onChangeText={setCustomColor}
              placeholder="#FFFFFF"
              placeholderTextColor="#8e8e93"
            />
            <TouchableOpacity
              style={[styles.colorPreview, { backgroundColor: customColor }]}
              onPress={() => {
                onSelectBackground({ type: 'solid', color: customColor });
                onClose();
              }}
            >
              <Text style={{ color: isLightColor(customColor) ? '#000' : '#fff' }}>
                Apply
              </Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

// Design Manager Modal (Save/Load/Export)
const DesignManagerModal = ({ visible, onClose, onSave, onLoad, onExport, currentDesignName, setDesignName }) => {
  const [savedDesigns, setSavedDesigns] = useState([]);
  const colorScheme = useColorScheme();

  useEffect(() => {
    loadSavedDesigns();
  }, [visible]);

  const loadSavedDesigns = async () => {
    try {
      const designs = await AsyncStorage.getItem('menuDesigns');
      if (designs) {
        setSavedDesigns(JSON.parse(designs));
      }
    } catch (e) {
      console.error('Error loading designs:', e);
    }
  };

  const deleteDesign = async (name) => {
    Alert.alert('Delete Design', `Are you sure you want to delete "${name}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          const updated = savedDesigns.filter((d) => d.name !== name);
          await AsyncStorage.setItem('menuDesigns', JSON.stringify(updated));
          setSavedDesigns(updated);
        },
      },
    ]);
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={[styles.modalContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7' }]}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Design Manager</Text>
          <TouchableOpacity onPress={onClose}>
            <MaterialIcons name="close" size={28} color={colorScheme === 'dark' ? '#fff' : '#000'} />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.designManagerContent}>
          <Text style={[styles.sectionTitle, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>
            Current Design
          </Text>
          <TextInput
            style={[styles.designNameInput, {
              backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff',
              color: colorScheme === 'dark' ? '#fff' : '#000'
            }]}
            value={currentDesignName}
            onChangeText={setDesignName}
            placeholder="Design name..."
            placeholderTextColor="#8e8e93"
          />

          <View style={styles.actionButtonsRow}>
            <TouchableOpacity style={styles.actionButton} onPress={onSave}>
              <MaterialIcons name="save" size={24} color="#fff" />
              <Text style={styles.actionButtonText}>Save</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.actionButton, { backgroundColor: '#34C759' }]} onPress={onExport}>
              <MaterialIcons name="file-download" size={24} color="#fff" />
              <Text style={styles.actionButtonText}>Export</Text>
            </TouchableOpacity>
          </View>

          <Text style={[styles.sectionTitle, { color: colorScheme === 'dark' ? '#fff' : '#000', marginTop: 20 }]}>
            Saved Designs
          </Text>
          {savedDesigns.length === 0 ? (
            <Text style={styles.noDesignsText}>No saved designs yet</Text>
          ) : (
            savedDesigns.map((design, index) => (
              <View key={index} style={[styles.savedDesignItem, { backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff' }]}>
                <View style={styles.savedDesignInfo}>
                  <Text style={[styles.savedDesignName, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>
                    {design.name}
                  </Text>
                  <Text style={styles.savedDesignDate}>
                    {new Date(design.savedAt).toLocaleDateString()}
                  </Text>
                </View>
                <View style={styles.savedDesignActions}>
                  <TouchableOpacity
                    style={styles.savedDesignButton}
                    onPress={() => {
                      onLoad(design);
                      onClose();
                    }}
                  >
                    <MaterialIcons name="open-in-new" size={20} color="#007AFF" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.savedDesignButton}
                    onPress={() => deleteDesign(design.name)}
                  >
                    <MaterialIcons name="delete" size={20} color="#FF3B30" />
                  </TouchableOpacity>
                </View>
              </View>
            ))
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

// Main Designer Screen
export default function DesignerScreen({ navigation }) {
  const colorScheme = useColorScheme();
  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
  const canvasRef = useRef(null);

  // Auth
  const [token, setToken] = useState("");

  useEffect(() => {
    async function fetchToken() {
      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken || "");
      } catch (ex) {
        console.error("Failed to load token:", ex);
      }
    }
    fetchToken();
  }, []);

  // State
  const [elements, setElements] = useState([]);
  const [selectedElementIds, setSelectedElementIds] = useState([]);
  const [selectionRect, setSelectionRect] = useState(null); // { startX, startY, endX, endY }
  const [background, setBackground] = useState(AI_BACKGROUNDS[0]);
  const [layoutTemplate, setLayoutTemplate] = useState('classic');
  const [designName, setDesignName] = useState('Untitled Menu');
  const [pageSize, setPageSize] = useState('A4');
  const [canvasSize, setCanvasSize] = useState({
    width: PAGE_SIZES.A4.screenWidth,
    height: PAGE_SIZES.A4.screenHeight
  });
  const [productStyles, setProductStyles] = useState(DEFAULT_PRODUCT_STYLES);
  const [productScale, setProductScale] = useState(1.0);
  const [borderStyles, setBorderStyles] = useState(DEFAULT_BORDER_STYLES);
  const [menuLanguage, setMenuLanguage] = useState('original');

  // Canvas zoom and pan state
  const [canvasZoom, setCanvasZoom] = useState(1.0);
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const lastPanPoint = useRef({ x: 0, y: 0 });

  // Track dragging state to disable scroll during element drag
  const [isDraggingElement, setIsDraggingElement] = useState(false);

  // Update canvas size when page size changes
  useEffect(() => {
    const size = PAGE_SIZES[pageSize];
    if (size) {
      setCanvasSize({ width: size.screenWidth, height: size.screenHeight });
    }
  }, [pageSize]);

  // Zoom controls
  const zoomIn = useCallback(() => {
    setCanvasZoom(prev => Math.min(prev + 0.25, 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setCanvasZoom(prev => Math.max(prev - 0.25, 0.25));
  }, []);

  const resetZoom = useCallback(() => {
    setCanvasZoom(1.0);
    setCanvasPan({ x: 0, y: 0 });
  }, []);

  const fitToScreen = useCallback(() => {
    const availableWidth = screenWidth - 32; // Account for padding
    const availableHeight = screenHeight - 200; // Account for header, toolbar, etc.
    const scaleX = availableWidth / canvasSize.width;
    const scaleY = availableHeight / canvasSize.height;
    const fitScale = Math.min(scaleX, scaleY, 1.0); // Don't zoom in beyond 100%
    setCanvasZoom(fitScale);
    setCanvasPan({ x: 0, y: 0 });
  }, [screenWidth, screenHeight, canvasSize]);

  // Selection rectangle handlers
  const selectionStartRef = useRef(null);

  const handleSelectionStart = useCallback((e) => {
    // Only start selection if clicking on canvas background (not on an element)
    if (e.target !== e.currentTarget) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / canvasZoom;
    const y = (e.clientY - rect.top) / canvasZoom;

    selectionStartRef.current = { x, y };
    setSelectionRect({ startX: x, startY: y, endX: x, endY: y });
  }, [canvasZoom]);

  const handleSelectionMove = useCallback((e) => {
    if (!selectionStartRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / canvasZoom;
    const y = (e.clientY - rect.top) / canvasZoom;

    setSelectionRect({
      startX: selectionStartRef.current.x,
      startY: selectionStartRef.current.y,
      endX: x,
      endY: y,
    });
  }, [canvasZoom]);

  const handleSelectionEnd = useCallback(() => {
    if (!selectionRect || !selectionStartRef.current) {
      selectionStartRef.current = null;
      setSelectionRect(null);
      return;
    }

    // Calculate selection bounds
    const left = Math.min(selectionRect.startX, selectionRect.endX);
    const right = Math.max(selectionRect.startX, selectionRect.endX);
    const top = Math.min(selectionRect.startY, selectionRect.endY);
    const bottom = Math.max(selectionRect.startY, selectionRect.endY);

    // Only select if rectangle is large enough (not just a click)
    if (right - left > 5 && bottom - top > 5) {
      // Find elements that intersect with the selection rectangle
      const selectedIds = elements.filter((el) => {
        const elLeft = el.x;
        const elRight = el.x + (el.width || 100);
        const elTop = el.y;
        const elBottom = el.y + (el.height || 50);

        // Check if element intersects with selection rectangle
        return !(elRight < left || elLeft > right || elBottom < top || elTop > bottom);
      }).map((el) => el.id);

      if (selectedIds.length > 0) {
        setSelectedElementIds(selectedIds);
      }
    }

    selectionStartRef.current = null;
    setSelectionRect(null);
  }, [selectionRect, elements]);

  // Handle mouse wheel zoom (web)
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setCanvasZoom(prev => Math.min(Math.max(prev + delta, 0.25), 3.0));
    }
  }, []);

  // Setup keyboard shortcuts and wheel zoom for web
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Plus: Zoom in
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        zoomIn();
      }
      // Ctrl/Cmd + Minus: Zoom out
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        zoomOut();
      }
      // Ctrl/Cmd + 0: Reset zoom
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        resetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [zoomIn, zoomOut, resetZoom]);

  // Track if this is the first render
  const isFirstRender = useRef(true);

  // Update all product elements when layout template changes
  useEffect(() => {
    // Skip the first render to avoid unnecessary updates
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const template = LAYOUT_TEMPLATES[layoutTemplate];
    if (!template) return;

    const style = template.productStyle;
    setElements((prev) => {
      const hasProducts = prev.some((el) => el.type === 'product');
      if (!hasProducts) return prev;

      return prev.map((el) => {
        if (el.type === 'product') {
          return {
            ...el,
            // Force new key by updating timestamp
            _layoutVersion: Date.now(),
            data: {
              ...el.data,
              layout: style.layout,
              showImage: style.showImage,
              showDescription: style.showDescription,
              showVolume: style.showVolume,
              imageSize: style.imageSize,
              fontSize: style.fontSize,
              padding: style.padding,
            },
          };
        }
        return el;
      });
    });
  }, [layoutTemplate]);

  // Modals
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [showAllergenEditor, setShowAllergenEditor] = useState(false);
  const [showElementStyleEditor, setShowElementStyleEditor] = useState(false);
  const [showBackgroundSelector, setShowBackgroundSelector] = useState(false);
  const [showDesignManager, setShowDesignManager] = useState(false);
  const [showLayoutSelector, setShowLayoutSelector] = useState(false);
  const [showPageSizeSelector, setShowPageSizeSelector] = useState(false);
  const [showProductStyleEditor, setShowProductStyleEditor] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize, setGridSize] = useState(20);

  // Data
  const { data: productsData } = useQuery(PRODUCTS_QUERY, {
    variables: { token },
    skip: !token,
  });

  const selectedElement = selectedElementIds.length === 1
    ? elements.find((e) => e.id === selectedElementIds[0])
    : null;

  // Element management
  const addElement = useCallback((type, data, style = {}, customWidth, customHeight) => {
    // Determine default sizes based on type
    let defaultWidth, defaultHeight;
    switch (type) {
      case 'product':
        defaultWidth = canvasSize.width - 40;
        defaultHeight = 100;
        break;
      case 'allergen-legend':
        defaultWidth = canvasSize.width - 40;
        defaultHeight = 80;
        break;
      case 'divider':
        defaultWidth = 200;
        defaultHeight = 2;
        break;
      case 'image':
        defaultWidth = 100;
        defaultHeight = 100;
        break;
      default:
        defaultWidth = undefined;
        defaultHeight = undefined;
    }

    const newElement = {
      id: Date.now().toString(),
      type,
      x: 20,
      y: elements.length * 60 + 20,
      width: customWidth !== undefined ? customWidth : defaultWidth,
      height: customHeight !== undefined ? customHeight : defaultHeight,
      data,
      style,
      zIndex: elements.length + 1,
    };
    setElements((prev) => [...prev, newElement]);
    setSelectedElementIds([newElement.id]);
  }, [elements.length, canvasSize.width]);

  const updateElement = useCallback((id, updates) => {
    setElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, ...updates } : el))
    );
  }, []);

  const deleteElement = useCallback((id) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
    setSelectedElementIds((prev) => prev.filter((eid) => eid !== id));
  }, []);

  // Delete all selected elements
  const deleteSelectedElements = useCallback(() => {
    setElements((prev) => prev.filter((el) => !selectedElementIds.includes(el.id)));
    setSelectedElementIds([]);
  }, [selectedElementIds]);

  const duplicateElement = useCallback((id) => {
    const element = elements.find((e) => e.id === id);
    if (element) {
      addElement(element.type, { ...element.data }, { ...element.style });
    }
  }, [elements, addElement]);

  // Product handlers
  const handleAddProduct = useCallback((productData) => {
    addElement('product', productData, {});
  }, [addElement]);

  // Save/Load/Export
  const saveDesign = async () => {
    try {
      const design = {
        name: designName,
        elements,
        background,
        layoutTemplate,
        pageSize,
        canvasSize,
        productStyles,
        productScale,
        borderStyles,
        menuLanguage,
        savedAt: new Date().toISOString(),
      };

      const existingDesigns = await AsyncStorage.getItem('menuDesigns');
      const designs = existingDesigns ? JSON.parse(existingDesigns) : [];
      const existingIndex = designs.findIndex((d) => d.name === designName);

      if (existingIndex >= 0) {
        designs[existingIndex] = design;
      } else {
        designs.push(design);
      }

      await AsyncStorage.setItem('menuDesigns', JSON.stringify(designs));
      Alert.alert('Success', 'Design saved successfully!');
    } catch (e) {
      Alert.alert('Error', 'Failed to save design');
      console.error(e);
    }
  };

  // Export design as JSON file for backup/sharing
  const exportDesignAsJSON = () => {
    if (Platform.OS !== 'web') {
      Alert.alert('Export', 'JSON export is only available on web.');
      return;
    }

    try {
      const design = {
        name: designName,
        elements,
        background,
        layoutTemplate,
        pageSize,
        canvasSize,
        productStyles,
        productScale,
        borderStyles,
        menuLanguage,
        exportedAt: new Date().toISOString(),
      };

      const jsonString = JSON.stringify(design, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${designName.replace(/[^a-z0-9]/gi, '_')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export JSON error:', error);
      Alert.alert('Error', 'Failed to export design as JSON');
    }
  };

  const loadDesign = (design) => {
    setDesignName(design.name);
    setElements(design.elements);
    setBackground(design.background);
    setLayoutTemplate(design.layoutTemplate);
    if (design.pageSize) {
      setPageSize(design.pageSize);
    }
    if (design.canvasSize) {
      setCanvasSize(design.canvasSize);
    }
    if (design.productStyles) {
      setProductStyles({
        ...design.productStyles,
        background: design.productStyles?.background || DEFAULT_PRODUCT_STYLES.background,
      });
    }
    if (design.productScale) {
      setProductScale(design.productScale);
    }
    if (design.borderStyles) {
      setBorderStyles(design.borderStyles);
    }
    if (design.menuLanguage) {
      setMenuLanguage(design.menuLanguage);
    }
  };

  // Helper function to convert image URL to base64
  const imageToBase64 = async (url) => {
    // Try fetch first (works if CORS headers are present)
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (response.ok) {
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
    } catch (fetchError) {
      console.warn('Fetch failed, trying canvas approach:', url);
    }

    // Fallback: use canvas to convert image
    return new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (e) {
          console.warn('Canvas conversion failed:', url, e);
          resolve(null);
        }
      };
      img.onerror = () => {
        console.warn('Image load failed:', url);
        resolve(null);
      };
      // Add timestamp to bypass cache and retry with CORS
      img.src = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();
    });
  };

  const exportDesign = async () => {
    if (Platform.OS !== 'web') {
      Alert.alert('Export', 'Image export is only available on web.');
      return;
    }

    if (isExporting) return;
    setIsExporting(true);

    try {
      // Get the canvas element
      const canvasElement = document.getElementById('menu-canvas');
      if (!canvasElement) {
        setIsExporting(false);
        Alert.alert('Error', 'Could not find canvas element.');
        return;
      }

      // Find all images and log them
      const images = canvasElement.querySelectorAll('img');
      console.log(`[Export] Found ${images.length} <img> tags`);

      // Note: Images will be captured by html2canvas with allowTaint
      // No need to convert to base64 since we're using native img tags

      // Wait a tick for images to render
      await new Promise(resolve => setTimeout(resolve, 100));

      // Dynamically import html2canvas
      const html2canvas = (await import('html2canvas')).default;

      // Get the actual canvas size (not the scaled screen size)
      const actualWidth = PAGE_SIZES[pageSize]?.width || canvasSize.width;
      const actualHeight = PAGE_SIZES[pageSize]?.height || canvasSize.height;

      // Calculate scale factor to get full resolution
      const scaleX = actualWidth / canvasSize.width;
      const scaleY = actualHeight / canvasSize.height;
      const scale = Math.max(scaleX, scaleY, 2); // At least 2x for quality

      // Capture the canvas at high resolution
      const canvas = await html2canvas(canvasElement, {
        scale: scale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${designName.replace(/[^a-z0-9]/gi, '_')}_${actualWidth}x${actualHeight}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png', 1.0);

      setIsExporting(false);
    } catch (error) {
      console.error('Export error:', error);
      setIsExporting(false);
      Alert.alert(
        'Export Error',
        'Failed to export image. Make sure html2canvas is installed: npm install html2canvas'
      );
    }
  };

  const applyAIDesign = (proposal) => {
    // Apply background
    setBackground(proposal.background);

    // Apply layout template
    setLayoutTemplate(proposal.layoutTemplate);

    // Apply product styles (preserve background setting)
    setProductStyles({
      ...proposal.productStyles,
      background: proposal.productStyles?.background || DEFAULT_PRODUCT_STYLES.background,
    });

    // Apply product scale
    if (proposal.productScale) {
      setProductScale(proposal.productScale);
    }

    // Apply border styles (default to no border if not specified)
    setBorderStyles(proposal.borderStyles || DEFAULT_BORDER_STYLES);

    // Generate allergen legend style based on the design
    const isDarkBackground = proposal.background?.baseColor &&
      (proposal.background.baseColor.startsWith('#0') ||
       proposal.background.baseColor.startsWith('#1') ||
       proposal.background.baseColor.startsWith('#2') ||
       proposal.background.baseColor.startsWith('#3'));

    const allergenStyle = {
      showTitle: true,
      showBorder: true,
      circleSize: 14,
      titleStyle: {
        fontSize: proposal.productStyles?.title?.fontSize ? proposal.productStyles.title.fontSize - 2 : 14,
        fontWeight: '600',
        color: proposal.productStyles?.title?.color || (isDarkBackground ? '#ffffff' : '#000000'),
      },
      labelStyle: {
        fontSize: proposal.productStyles?.description?.fontSize ? proposal.productStyles.description.fontSize - 2 : 12,
        color: proposal.productStyles?.description?.color || (isDarkBackground ? '#cccccc' : '#333333'),
      },
      backgroundColor: 'transparent',
    };

    // Generate text styles based on the design
    const textColor = proposal.productStyles?.title?.color || (isDarkBackground ? '#ffffff' : '#000000');
    const secondaryTextColor = proposal.productStyles?.description?.color || (isDarkBackground ? '#cccccc' : '#333333');
    const accentColor = proposal.productStyles?.price?.color || proposal.borderStyles?.color || textColor;

    // Update existing product elements with new layout properties
    // Also update allergen-legend and text elements with matching styles
    const layoutStyle = LAYOUT_TEMPLATES[proposal.layoutTemplate]?.productStyle;
    setElements((prev) =>
      prev.map((el) => {
        if (el.type === 'product' && layoutStyle) {
          return {
            ...el,
            _layoutVersion: Date.now(),
            data: {
              ...el.data,
              layout: layoutStyle.layout,
              showImage: layoutStyle.showImage,
              showDescription: layoutStyle.showDescription,
              showVolume: layoutStyle.showVolume,
              imageSize: layoutStyle.imageSize,
              fontSize: layoutStyle.fontSize,
              padding: layoutStyle.padding,
            },
          };
        }
        if (el.type === 'allergen-legend') {
          return {
            ...el,
            _layoutVersion: Date.now(),
            data: {
              ...el.data,
              legendStyle: allergenStyle,
            },
          };
        }
        if (el.type === 'text') {
          // Determine if this is a heading (large font) or body text
          const currentFontSize = el.style?.fontSize || 16;
          const isHeading = currentFontSize >= 24;

          // Apply matching styles based on text type
          const newStyle = isHeading
            ? {
                ...el.style,
                color: textColor,
                fontWeight: proposal.productStyles?.title?.fontWeight || 'bold',
              }
            : {
                ...el.style,
                color: secondaryTextColor,
                fontWeight: proposal.productStyles?.description?.fontWeight || 'normal',
              };

          return {
            ...el,
            _layoutVersion: Date.now(),
            style: newStyle,
          };
        }
        return el;
      })
    );

    setShowAISuggestions(false);
  };

  const printMenu = useCallback(() => {
    if (Platform.OS !== 'web') {
      Alert.alert('Print', 'Printing is only available on web platform.');
      return;
    }

    // Find the canvas element using nativeID
    const canvasElement = document.getElementById('menu-canvas');
    if (!canvasElement) {
      Alert.alert('Error', 'Could not find the menu canvas for printing.');
      return;
    }

    // Get the page size info
    const pageSizeInfo = PAGE_SIZES[pageSize];
    const pageName = pageSizeInfo?.name || 'A4';
    const widthMM = pageSizeInfo?.width || 210;
    const heightMM = pageSizeInfo?.height || 297;

    // Calculate scale to fill the page
    const mmToPx = 3.7795275591;
    const pageWidthPx = widthMM * mmToPx;
    const pageHeightPx = heightMM * mmToPx;
    const scale = Math.min(pageWidthPx / canvasSize.width, pageHeightPx / canvasSize.height);

    // Function to clone element with computed styles
    const cloneWithStyles = (element, isRoot = false) => {
      const clone = element.cloneNode(false);
      const computedStyle = window.getComputedStyle(element);

      // Copy important computed styles as inline styles
      const importantProps = [
        'display', 'flex-direction', 'justify-content', 'align-items',
        'position', 'background-color', 'background-image', 'background',
        'color', 'font-size', 'font-weight', 'font-family',
        'padding', 'margin', 'border', 'border-radius',
        'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
        'overflow', 'box-shadow', 'opacity', 'z-index',
        'top', 'left', 'right', 'bottom',
        'text-align', 'line-height', 'letter-spacing',
        'gap', 'flex', 'flex-wrap', 'flex-grow', 'flex-shrink',
        'transform', 'transform-origin',
      ];

      let cssText = '';
      for (const prop of importantProps) {
        const value = computedStyle.getPropertyValue(prop);
        // Don't filter out transform values - they're important for positioning
        if (prop === 'transform' || prop === 'transform-origin') {
          if (value && value !== 'none') {
            cssText += `${prop}:${value};`;
          }
        } else if (value && value !== 'none' && value !== 'auto' && value !== 'normal') {
          cssText += `${prop}:${value};`;
        }
      }

      // Apply styles
      clone.style.cssText = cssText;

      // For root element, apply the scale transform
      if (isRoot) {
        clone.style.transform = `scale(${scale})`;
        clone.style.transformOrigin = 'top left';
        clone.style.width = `${canvasSize.width}px`;
        clone.style.height = `${canvasSize.height}px`;
      }

      // Remove selection border
      if (clone.style.border && clone.style.border.includes('2px')) {
        clone.style.border = 'none';
      }

      // Recursively clone children
      for (const child of element.childNodes) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          clone.appendChild(cloneWithStyles(child, false));
        } else if (child.nodeType === Node.TEXT_NODE) {
          clone.appendChild(child.cloneNode(true));
        }
      }

      return clone;
    };

    // Clone the canvas with all computed styles
    const clonedCanvas = cloneWithStyles(canvasElement, true);

    // Create print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      Alert.alert('Error', 'Could not open print window. Please allow popups for this site.');
      return;
    }

    // Write the print document - content sized to fill the page
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Menu - ${designName || 'Untitled'}</title>
          <style>
            @page {
              size: ${widthMM}mm ${heightMM}mm;
              margin: 0;
            }

            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
              box-sizing: border-box;
            }

            html, body {
              margin: 0;
              padding: 0;
            }

            .print-notice {
              background: #fffbcc;
              border: 1px solid #e6d980;
              padding: 12px 16px;
              margin: 10px;
              border-radius: 6px;
              font-family: system-ui, -apple-system, sans-serif;
              font-size: 14px;
            }

            .print-notice strong {
              color: #856404;
            }

            #print-canvas {
              width: ${widthMM}mm;
              height: ${heightMM}mm;
              position: relative;
              overflow: hidden;
              margin: 0 auto;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              background: white;
            }

            @media print {
              .print-notice {
                display: none !important;
              }

              #print-canvas {
                box-shadow: none;
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-notice">
            <strong>Paper Size:</strong> Select <strong>${pageName}</strong> (${widthMM} x ${heightMM} mm) in your print settings, and set margins to <strong>None</strong>.
          </div>
          <div id="print-canvas"></div>
        </body>
      </html>
    `);

    printWindow.document.close();

    // Append the cloned canvas inside the print-canvas container
    const printContainer = printWindow.document.getElementById('print-canvas');
    printContainer.appendChild(clonedCanvas);

    // Wait for images to load then print
    const images = printWindow.document.getElementsByTagName('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise(resolve => {
        img.onload = resolve;
        img.onerror = resolve;
        setTimeout(resolve, 2000);
      });
    });

    Promise.all(imagePromises).then(() => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 300);
    });
  }, [pageSize, canvasSize, designName]);

  // Import design from file
  const importDesign = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.All,
      });

      if (!result.canceled) {
        // In a real implementation, this would parse a JSON file
        Alert.alert('Import', 'Design import functionality would load a previously exported design file.');
      }
    } catch (e) {
      Alert.alert('Error', 'Failed to import design');
    }
  };

  // Add image element from picker
  const addImageElement = async () => {
    try {
      // Request permission first (needed on some platforms)
      if (Platform.OS !== 'web') {
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== 'granted') {
          Alert.alert('Permission needed', 'Please grant permission to access your photos.');
          return;
        }
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        quality: 1,
      });

      console.log('ImagePicker result:', result);

      if (!result.canceled && result.assets && result.assets[0]) {
        const asset = result.assets[0];
        console.log('Selected asset:', asset);

        // Calculate aspect ratio to set initial size
        // On web, width/height might not be available, so use defaults
        const maxSize = 150;
        const hasValidDimensions = asset.width && asset.height && asset.width > 0 && asset.height > 0;
        const aspectRatio = hasValidDimensions ? asset.width / asset.height : 1;
        let width, height;

        if (aspectRatio > 1) {
          width = maxSize;
          height = maxSize / aspectRatio;
        } else {
          height = maxSize;
          width = maxSize * aspectRatio;
        }

        console.log('Adding image element with dimensions:', { width, height, aspectRatio, uri: asset.uri });

        const newId = Date.now().toString();

        setElements((prev) => {
          const newElement = {
            id: newId,
            type: 'image',
            x: 50,
            y: 50,
            width: width,
            height: height,
            data: {
              uri: asset.uri,
              originalWidth: asset.width || width,
              originalHeight: asset.height || height,
              aspectRatio: aspectRatio,
            },
            style: {},
            zIndex: prev.length + 1,
          };
          console.log('Image element added:', newElement);
          return [...prev, newElement];
        });
        setSelectedElementIds([newId]);
      } else {
        console.log('Image picker was canceled or no assets');
      }
    } catch (e) {
      console.error('Error picking image:', e);
      Alert.alert('Error', 'Failed to add image: ' + e.message);
    }
  };

  // Render background
  const renderBackground = () => {
    if (background.type === 'image') {
      return (
        <Image
          source={{ uri: background.uri, cache: 'default' }}
          style={StyleSheet.absoluteFill}
          resizeMode="contain"
        />
      );
    } else if (background.type === 'solid') {
      return <View style={[StyleSheet.absoluteFill, { backgroundColor: background.color }]} />;
    } else if (background.pattern || background.category) {
      // AI-generated background with pattern
      return (
        <BackgroundPatternRenderer
          background={background}
          width={canvasSize.width}
          height={canvasSize.height}
        />
      );
    } else {
      return <View style={[StyleSheet.absoluteFill, { backgroundColor: background.baseColor || background.color || '#FFFFFF' }]} />;
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colorScheme === 'dark' ? '#000' : '#f2f2f7' }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#ffffff' }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerButton}>
          <MaterialIcons name="arrow-back" size={24} color={colorScheme === 'dark' ? '#fff' : '#000'} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={[styles.headerTitle, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>
            Designer
          </Text>
          <TouchableOpacity onPress={() => setShowPageSizeSelector(true)} style={styles.pageSizeBadge}>
            <Text style={styles.pageSizeBadgeText}>{PAGE_SIZES[pageSize]?.name || 'A4'}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setShowLanguageSelector(true)} style={[styles.pageSizeBadge, { marginLeft: 8 }]}>
            <Text style={styles.pageSizeBadgeText}>
              {MENU_LANGUAGES.find(l => l.code === menuLanguage)?.flag || '🌐'} {MENU_LANGUAGES.find(l => l.code === menuLanguage)?.name || 'Language'}
            </Text>
          </TouchableOpacity>
        </View>
        <TouchableOpacity onPress={exportDesign} style={[styles.headerButton, isExporting && { opacity: 0.5 }]} disabled={isExporting}>
          {isExporting ? (
            <MaterialIcons name="hourglass-empty" size={24} color={colorScheme === 'dark' ? '#fff' : '#000'} />
          ) : (
            <MaterialIcons name="file-download" size={24} color={colorScheme === 'dark' ? '#fff' : '#000'} />
          )}
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setShowDesignManager(true)} style={styles.headerButton}>
          <MaterialIcons name="folder" size={24} color={colorScheme === 'dark' ? '#fff' : '#000'} />
        </TouchableOpacity>
      </View>

      {/* Toolbar */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.toolbar}>
        <TouchableOpacity style={styles.toolbarButton} onPress={() => setShowProductSearch(true)}>
          <MaterialIcons name="restaurant-menu" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Product</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.toolbarButton}
          onPress={() => addElement('text', { text: 'New Text' }, TEXT_STYLES.body)}
        >
          <MaterialIcons name="text-fields" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Text</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.toolbarButton}
          onPress={() => addElement('divider', {}, { color: '#000000', thickness: 2 })}
        >
          <MaterialIcons name="remove" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Divider</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.toolbarButton}
          onPress={() => addElement('allergen-legend', {}, {})}
        >
          <MaterialIcons name="info" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Allergens</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.toolbarButton} onPress={addImageElement}>
          <MaterialIcons name="image" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Image</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.toolbarButton} onPress={() => setShowBackgroundSelector(true)}>
          <MaterialIcons name="wallpaper" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Background</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.toolbarButton} onPress={() => setShowLayoutSelector(true)}>
          <MaterialIcons name="view-quilt" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Layout</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.toolbarButton} onPress={() => setShowProductStyleEditor(true)}>
          <MaterialIcons name="format-size" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Styles</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.toolbarButton} onPress={() => setShowPageSizeSelector(true)}>
          <MaterialIcons name="crop-free" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Page Size</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.toolbarButton} onPress={importDesign}>
          <MaterialIcons name="file-upload" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Import</Text>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.toolbarButton, { backgroundColor: '#fff3e0', borderRadius: 8 }]} onPress={() => setShowAISuggestions(true)}>
          <MaterialIcons name="auto-awesome" size={20} color="#ff9800" />
          <Text style={[styles.toolbarButtonText, { color: '#ff9800' }]}>AI Suggest</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.toolbarButton, showGrid && { backgroundColor: '#e3f2fd', borderRadius: 8 }]}
          onPress={() => setShowGrid(!showGrid)}
        >
          <MaterialIcons name="grid-on" size={20} color={showGrid ? '#1976D2' : '#007AFF'} />
          <Text style={[styles.toolbarButtonText, showGrid && { color: '#1976D2' }]}>Grid</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.toolbarButton} onPress={printMenu}>
          <MaterialIcons name="print" size={20} color="#007AFF" />
          <Text style={styles.toolbarButtonText}>Print</Text>
        </TouchableOpacity>
      </ScrollView>

      {/* Canvas Area with Zoom/Pan */}
      <View style={styles.canvasArea}>
        <ScrollView
          style={styles.canvasScrollContainer}
          contentContainerStyle={[styles.canvasScrollContent, {
            minWidth: canvasSize.width * canvasZoom + 32,
            minHeight: canvasSize.height * canvasZoom + 32,
          }]}
          horizontal={false}
          showsVerticalScrollIndicator={true}
          scrollEnabled={!isDraggingElement}
        >
          <ScrollView
            horizontal={true}
            showsHorizontalScrollIndicator={true}
            contentContainerStyle={[styles.canvasHorizontalContent, {
              minWidth: canvasSize.width * canvasZoom + 32,
            }]}
            scrollEnabled={!isDraggingElement}
          >
            <View
              style={{
                transform: [
                  { translateX: canvasPan.x },
                  { translateY: canvasPan.y },
                  { scale: canvasZoom },
                ],
                transformOrigin: 'top left',
                backgroundColor: 'transparent',
              }}
            >
              <View
                ref={canvasRef}
                nativeID="menu-canvas"
                onStartShouldSetResponder={(e) => e.target === e.currentTarget}
                onResponderRelease={() => {
                  if (!selectionRect) {
                    setSelectedElementIds([]);
                  }
                }}
                onMouseDown={(e) => {
                  // Check if clicked element is a draggable element
                  let target = e.target;
                  while (target && target !== e.currentTarget) {
                    if (target.getAttribute && target.getAttribute('data-draggable') === 'true') {
                      return; // Clicked on a draggable element, don't start selection
                    }
                    target = target.parentElement;
                  }
                  handleSelectionStart(e);
                }}
                onMouseMove={handleSelectionMove}
                onMouseUp={handleSelectionEnd}
                onMouseLeave={handleSelectionEnd}
                onClick={(e) => {
                  // Web fallback: only deselect if clicking on canvas or background elements
                  // Check if clicked element is NOT a draggable element
                  let target = e.target;
                  while (target && target !== e.currentTarget) {
                    if (target.getAttribute && target.getAttribute('data-draggable') === 'true') {
                      return; // Clicked on a draggable element, don't deselect
                    }
                    target = target.parentElement;
                  }
                  if (!selectionRect) {
                    setSelectedElementIds([]);
                  }
                }}
                style={[
                  styles.canvas,
                  {
                    width: canvasSize.width,
                    height: canvasSize.height,
                  },
                ]}
              >
                {renderBackground()}
                {/* Alignment Grid - hidden during export */}
                {showGrid && !isExporting && (
                  <View style={StyleSheet.absoluteFill} pointerEvents="none">
                    {/* Vertical lines */}
                    {Array.from({ length: Math.floor(canvasSize.width / gridSize) + 1 }).map((_, i) => (
                      <View
                        key={`v-${i}`}
                        style={{
                          position: 'absolute',
                          left: i * gridSize,
                          top: 0,
                          width: 1,
                          height: '100%',
                          backgroundColor: i % 5 === 0 ? 'rgba(0, 122, 255, 0.3)' : 'rgba(0, 122, 255, 0.15)',
                        }}
                      />
                    ))}
                    {/* Horizontal lines */}
                    {Array.from({ length: Math.floor(canvasSize.height / gridSize) + 1 }).map((_, i) => (
                      <View
                        key={`h-${i}`}
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: i * gridSize,
                          width: '100%',
                          height: 1,
                          backgroundColor: i % 5 === 0 ? 'rgba(0, 122, 255, 0.3)' : 'rgba(0, 122, 255, 0.15)',
                        }}
                      />
                    ))}
                    {/* Center lines - snapped to nearest grid line */}
                    <View
                      style={{
                        position: 'absolute',
                        left: Math.round(canvasSize.width / 2 / gridSize) * gridSize,
                        top: 0,
                        width: 1,
                        height: '100%',
                        backgroundColor: 'rgba(255, 0, 0, 0.5)',
                      }}
                    />
                    <View
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: Math.round(canvasSize.height / 2 / gridSize) * gridSize,
                        width: '100%',
                        height: 1,
                        backgroundColor: 'rgba(255, 0, 0, 0.5)',
                      }}
                    />
                  </View>
                )}
                {elements.map((element) => (
                  <DraggableElement
                    key={`${element.id}-${element._layoutVersion || 0}-${menuLanguage}`}
                    element={element}
                    elements={elements}
                    onUpdate={updateElement}
                    onSelect={(id, addToSelection) => {
                      if (addToSelection) {
                        // Shift+click: toggle in selection
                        setSelectedElementIds((prev) =>
                          prev.includes(id)
                            ? prev.filter((eid) => eid !== id)
                            : [...prev, id]
                        );
                      } else {
                        // Normal click: select only this element
                        setSelectedElementIds([id]);
                      }
                    }}
                    isSelected={selectedElementIds.includes(element.id)}
                    selectedElementIds={selectedElementIds}
                    canvasSize={canvasSize}
                    productStyles={productStyles}
                    productScale={productScale}
                    borderStyles={borderStyles}
                    menuLanguage={menuLanguage}
                    background={background}
                    onDragStart={() => setIsDraggingElement(true)}
                    onDragEnd={() => setIsDraggingElement(false)}
                    onMoveSelected={(dx, dy) => {
                      // Move all selected elements by the same delta
                      setElements((prev) =>
                        prev.map((el) =>
                          selectedElementIds.includes(el.id)
                            ? { ...el, x: el.x + dx, y: el.y + dy }
                            : el
                        )
                      );
                    }}
                    onDoubleClick={(el) => {
                      if (el.type === 'text') {
                        setSelectedElementIds([el.id]);
                        setShowTextEditor(true);
                      } else if (el.type === 'allergen-legend') {
                        setSelectedElementIds([el.id]);
                        setShowAllergenEditor(true);
                      }
                    }}
                  />
                ))}
                {/* Selection Rectangle */}
                {selectionRect && (
                  <View
                    pointerEvents="none"
                    style={{
                      position: 'absolute',
                      left: Math.min(selectionRect.startX, selectionRect.endX),
                      top: Math.min(selectionRect.startY, selectionRect.endY),
                      width: Math.abs(selectionRect.endX - selectionRect.startX),
                      height: Math.abs(selectionRect.endY - selectionRect.startY),
                      backgroundColor: 'rgba(0, 122, 255, 0.1)',
                      borderWidth: 1,
                      borderColor: 'rgba(0, 122, 255, 0.5)',
                      borderStyle: 'dashed',
                    }}
                  />
                )}
              </View>
            </View>
          </ScrollView>
        </ScrollView>

        {/* Zoom Controls */}
        <View style={styles.zoomControls}>
          <TouchableOpacity style={styles.zoomButton} onPress={zoomIn}>
            <MaterialIcons name="add" size={20} color="#007AFF" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.zoomButton} onPress={zoomOut}>
            <MaterialIcons name="remove" size={20} color="#007AFF" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.zoomButton} onPress={fitToScreen}>
            <MaterialIcons name="fit-screen" size={20} color="#007AFF" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.zoomButton} onPress={resetZoom}>
            <MaterialIcons name="center-focus-strong" size={20} color="#007AFF" />
          </TouchableOpacity>
          <Text style={styles.zoomText}>{Math.round(canvasZoom * 100)}%</Text>
        </View>
      </View>

      {/* Selected Element Actions */}
      {selectedElementIds.length > 0 && (
        <View style={[styles.selectedActions, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#ffffff' }]}>
          {selectedElementIds.length > 1 && (
            <Text style={{ color: colorScheme === 'dark' ? '#fff' : '#666', marginRight: 12, fontSize: 12 }}>
              {selectedElementIds.length} selected
            </Text>
          )}
          {selectedElement?.type === 'text' && (
            <TouchableOpacity
              style={styles.selectedActionButton}
              onPress={() => setShowTextEditor(true)}
            >
              <MaterialIcons name="edit" size={20} color="#007AFF" />
              <Text style={styles.selectedActionText}>Edit</Text>
            </TouchableOpacity>
          )}
          {selectedElement?.type === 'product' && (
            <TouchableOpacity
              style={styles.selectedActionButton}
              onPress={() => setShowElementStyleEditor(true)}
            >
              <MaterialIcons name="format-color-fill" size={20} color="#007AFF" />
              <Text style={styles.selectedActionText}>Style</Text>
            </TouchableOpacity>
          )}
          {selectedElementIds.length === 1 && (
            <TouchableOpacity
              style={styles.selectedActionButton}
              onPress={() => duplicateElement(selectedElementIds[0])}
            >
              <MaterialIcons name="content-copy" size={20} color="#007AFF" />
              <Text style={styles.selectedActionText}>Duplicate</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={styles.selectedActionButton}
            onPress={deleteSelectedElements}
          >
            <MaterialIcons name="delete" size={20} color="#FF3B30" />
            <Text style={[styles.selectedActionText, { color: '#FF3B30' }]}>
              Delete{selectedElementIds.length > 1 ? ` (${selectedElementIds.length})` : ''}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Page Size Selector Modal */}
      <Modal visible={showPageSizeSelector} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={[styles.modalContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7' }]}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Page Size</Text>
            <TouchableOpacity onPress={() => setShowPageSizeSelector(false)}>
              <MaterialIcons name="close" size={28} color={colorScheme === 'dark' ? '#fff' : '#000'} />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.layoutOptions}>
            {Object.entries(PAGE_SIZES).map(([key, size]) => (
              <TouchableOpacity
                key={key}
                style={[
                  styles.pageSizeOption,
                  { backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff' },
                  pageSize === key && styles.pageSizeOptionActive,
                ]}
                onPress={() => {
                  setPageSize(key);
                  setShowPageSizeSelector(false);
                }}
              >
                <View style={styles.pageSizePreview}>
                  <View
                    style={[
                      styles.pageSizePreviewBox,
                      {
                        width: size.screenWidth / 10,
                        height: size.screenHeight / 10,
                        borderColor: pageSize === key ? '#007AFF' : colorScheme === 'dark' ? '#666' : '#ccc',
                      },
                    ]}
                  />
                </View>
                <View style={styles.pageSizeInfo}>
                  <Text
                    style={[
                      styles.pageSizeText,
                      { color: pageSize === key ? '#007AFF' : colorScheme === 'dark' ? '#fff' : '#000' },
                    ]}
                  >
                    {size.name}
                  </Text>
                  <Text style={styles.pageSizeDesc}>{size.description}</Text>
                </View>
                {pageSize === key && (
                  <MaterialIcons name="check-circle" size={24} color="#007AFF" />
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Layout Selector Modal */}
      <Modal visible={showLayoutSelector} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={[styles.modalContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7' }]}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Product Layout</Text>
            <TouchableOpacity onPress={() => setShowLayoutSelector(false)}>
              <MaterialIcons name="close" size={28} color={colorScheme === 'dark' ? '#fff' : '#000'} />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.layoutOptions}>
            {Object.entries(LAYOUT_TEMPLATES).map(([key, template]) => (
              <TouchableOpacity
                key={key}
                style={[
                  styles.layoutOption,
                  { backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff' },
                  layoutTemplate === key && styles.layoutOptionActive,
                ]}
                onPress={() => {
                  setLayoutTemplate(key);
                  setShowLayoutSelector(false);
                }}
              >
                <MaterialIcons
                  name={template.icon}
                  size={32}
                  color={layoutTemplate === key ? '#007AFF' : colorScheme === 'dark' ? '#fff' : '#000'}
                />
                <Text
                  style={[
                    styles.layoutOptionText,
                    { color: layoutTemplate === key ? '#007AFF' : colorScheme === 'dark' ? '#fff' : '#000' },
                  ]}
                >
                  {template.name}
                </Text>
                <Text style={styles.layoutOptionDesc}>
                  {template.productStyle.showImage ? 'With images' : 'No images'} |
                  {template.productStyle.showDescription ? ' Descriptions' : ' No desc'} |
                  Font: {template.productStyle.fontSize}px
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Modals */}
      <ProductSearchModal
        visible={showProductSearch}
        onClose={() => setShowProductSearch(false)}
        onSelectProduct={handleAddProduct}
        products={productsData?.products || []}
        layoutTemplate={layoutTemplate}
      />

      <TextStyleEditorModal
        visible={showTextEditor}
        onClose={() => setShowTextEditor(false)}
        element={selectedElement}
        onUpdate={updateElement}
      />

      <AllergenStyleEditorModal
        visible={showAllergenEditor}
        onClose={() => setShowAllergenEditor(false)}
        element={selectedElement}
        onUpdate={updateElement}
      />

      <ElementStyleEditorModal
        visible={showElementStyleEditor}
        onClose={() => setShowElementStyleEditor(false)}
        element={selectedElement}
        onUpdate={updateElement}
        globalStyles={productStyles}
      />

      <BackgroundSelectorModal
        visible={showBackgroundSelector}
        onClose={() => setShowBackgroundSelector(false)}
        onSelectBackground={setBackground}
        currentBackground={background}
      />

      <DesignManagerModal
        visible={showDesignManager}
        onClose={() => setShowDesignManager(false)}
        onSave={saveDesign}
        onLoad={loadDesign}
        onExport={exportDesignAsJSON}
        currentDesignName={designName}
        setDesignName={setDesignName}
      />

      <ProductStyleEditorModal
        visible={showProductStyleEditor}
        onClose={() => setShowProductStyleEditor(false)}
        productStyles={productStyles}
        onUpdateStyles={setProductStyles}
        productScale={productScale}
        onUpdateScale={setProductScale}
        borderStyles={borderStyles}
        onUpdateBorderStyles={setBorderStyles}
      />

      {/* AI Suggestions Modal */}
      <Modal visible={showAISuggestions} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={[styles.modalContainer, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#f2f2f7' }]}>
          <View style={[styles.modalHeader, { backgroundColor: colorScheme === 'dark' ? '#1c1c1e' : '#ffffff' }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <MaterialIcons name="auto-awesome" size={24} color="#ff9800" style={{ marginRight: 8 }} />
              <Text style={[styles.modalTitle, { color: colorScheme === 'dark' ? '#fff' : '#000' }]}>
                AI Design Suggestions
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowAISuggestions(false)}>
              <MaterialIcons name="close" size={24} color={colorScheme === 'dark' ? '#fff' : '#000'} />
            </TouchableOpacity>
          </View>

          <Text style={{ padding: 16, color: colorScheme === 'dark' ? '#aaa' : '#666', fontSize: 14 }}>
            Choose a design theme to instantly transform your menu
          </Text>

          <ScrollView contentContainerStyle={{ padding: 12 }}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              {AI_DESIGN_PROPOSALS.map((proposal) => (
                <TouchableOpacity
                  key={proposal.id}
                  style={{
                    width: '48%',
                    marginBottom: 16,
                    backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#fff',
                    borderRadius: 12,
                    overflow: 'hidden',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 4,
                    elevation: 3,
                  }}
                  onPress={() => applyAIDesign(proposal)}
                >
                  {/* Preview */}
                  <View
                    style={{
                      height: 100,
                      backgroundColor: proposal.preview.background,
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderBottomWidth: 1,
                      borderBottomColor: colorScheme === 'dark' ? '#3c3c3e' : '#e0e0e0',
                    }}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          backgroundColor: proposal.preview.accent,
                          borderRadius: 8,
                          marginRight: 12,
                        }}
                      />
                      <View>
                        <View
                          style={{
                            width: 60,
                            height: 8,
                            backgroundColor: proposal.productStyles.title.color,
                            borderRadius: 4,
                            marginBottom: 4,
                          }}
                        />
                        <View
                          style={{
                            width: 40,
                            height: 6,
                            backgroundColor: proposal.productStyles.description.color,
                            borderRadius: 3,
                            opacity: 0.6,
                          }}
                        />
                      </View>
                      <Text
                        style={{
                          color: proposal.productStyles.price.color,
                          fontWeight: 'bold',
                          fontSize: 14,
                          marginLeft: 12,
                        }}
                      >
                        12.99
                      </Text>
                    </View>
                  </View>

                  {/* Info */}
                  <View style={{ padding: 12 }}>
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: colorScheme === 'dark' ? '#fff' : '#000',
                        marginBottom: 4,
                      }}
                    >
                      {proposal.name}
                    </Text>
                    <Text
                      style={{
                        fontSize: 12,
                        color: colorScheme === 'dark' ? '#888' : '#666',
                      }}
                    >
                      {proposal.description}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Language Selector Modal */}
      <Modal visible={showLanguageSelector} animationType="fade" transparent={true}>
        <Pressable
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}
          onPress={() => setShowLanguageSelector(false)}
        >
          <View style={{
            backgroundColor: colorScheme === 'dark' ? '#2c2c2e' : '#ffffff',
            borderRadius: 16,
            padding: 20,
            width: '80%',
            maxWidth: 350,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 5,
          }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Text style={{ fontSize: 18, fontWeight: '600', color: colorScheme === 'dark' ? '#fff' : '#000' }}>
                Menu Language
              </Text>
              <TouchableOpacity onPress={() => setShowLanguageSelector(false)}>
                <MaterialIcons name="close" size={24} color={colorScheme === 'dark' ? '#fff' : '#000'} />
              </TouchableOpacity>
            </View>
            <Text style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
              Select the language for product names and descriptions
            </Text>
            {MENU_LANGUAGES.map((lang) => (
              <TouchableOpacity
                key={lang.code}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: 12,
                  borderRadius: 10,
                  marginBottom: 8,
                  backgroundColor: menuLanguage === lang.code
                    ? (colorScheme === 'dark' ? '#007AFF33' : '#007AFF15')
                    : 'transparent',
                  borderWidth: menuLanguage === lang.code ? 2 : 1,
                  borderColor: menuLanguage === lang.code ? '#007AFF' : (colorScheme === 'dark' ? '#3c3c3e' : '#e0e0e0'),
                }}
                onPress={() => {
                  setMenuLanguage(lang.code);
                  setShowLanguageSelector(false);
                }}
              >
                <Text style={{ fontSize: 24, marginRight: 12 }}>{lang.flag}</Text>
                <Text style={{
                  fontSize: 16,
                  fontWeight: menuLanguage === lang.code ? '600' : 'normal',
                  color: menuLanguage === lang.code ? '#007AFF' : (colorScheme === 'dark' ? '#fff' : '#000'),
                }}>
                  {lang.name}
                </Text>
                {menuLanguage === lang.code && (
                  <MaterialIcons name="check-circle" size={20} color="#007AFF" style={{ marginLeft: 'auto' }} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerButton: {
    padding: 4,
  },
  headerCenter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'transparent',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  pageSizeBadge: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  pageSizeBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  toolbar: {
    flexGrow: 0,
    flexShrink: 0,
    paddingHorizontal: 4,
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  toolbarButton: {
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  toolbarButtonText: {
    fontSize: 10,
    color: '#007AFF',
    marginTop: 2,
  },
  canvasArea: {
    flex: 1,
    position: 'relative',
  },
  canvasScrollContainer: {
    flex: 1,
    backgroundColor: '#e5e5e5',
  },
  zoomControls: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
    gap: 4,
  },
  zoomButton: {
    padding: 8,
    borderRadius: 4,
  },
  zoomText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    minWidth: 40,
    textAlign: 'center',
  },
  canvasScrollContent: {
    flexGrow: 1,
    padding: 8,
    backgroundColor: '#e5e5e5',
  },
  canvasHorizontalContent: {
    flexGrow: 1,
    padding: 8,
    backgroundColor: '#e5e5e5',
  },
  canvas: {
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  draggableElement: {
    position: 'absolute',
    padding: 4,
    borderRadius: 4,
    cursor: 'grab',
    backgroundColor: 'transparent',
    // Prevent browser touch actions from interfering with drag on web
    ...(Platform.OS === 'web' ? { touchAction: 'none', userSelect: 'none' } : {}),
  },
  productElement: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    backgroundColor: 'transparent',
  },
  productTitle: {
    fontWeight: '600',
  },
  productSubtitle: {
    color: '#666',
    marginTop: 2,
  },
  productVolume: {
    color: '#888',
    marginTop: 2,
  },
  productPrice: {
    fontWeight: 'bold',
  },
  resizeHandle: {
    position: 'absolute',
    right: -8,
    bottom: -8,
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 2,
  },
  cornerResizeHandle: {
    position: 'absolute',
    right: -12,
    bottom: -12,
    backgroundColor: '#007AFF',
    borderRadius: 12,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
    elevation: 5,
    cursor: 'se-resize',
  },
  selectedActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  selectedActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  selectedActionText: {
    marginLeft: 6,
    color: '#007AFF',
    fontWeight: '500',
  },
  modalContainer: {
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: 'transparent',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  searchInput: {
    margin: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 10,
    fontSize: 16,
  },
  productList: {
    flex: 1,
    paddingHorizontal: 16,
  },
  productSearchItem: {
    flexDirection: 'row',
    padding: 12,
    borderRadius: 10,
    marginBottom: 8,
  },
  productSearchImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
  },
  productSearchInfo: {
    flex: 1,
    marginLeft: 12,
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  productSearchTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  productSearchSubtitle: {
    fontSize: 14,
    color: '#666',
    marginTop: 2,
  },
  productSearchPrice: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#007AFF',
    marginTop: 4,
  },
  editorContent: {
    flex: 1,
    padding: 16,
  },
  editorLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  textEditInput: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 10,
    fontSize: 16,
    minHeight: 100,
    textAlignVertical: 'top',
  },
  optionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  optionButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#007AFF',
    marginRight: 8,
    marginBottom: 8,
  },
  optionButtonActive: {
    backgroundColor: '#007AFF',
  },
  optionButtonText: {
    color: '#007AFF',
    fontWeight: '500',
  },
  optionButtonTextActive: {
    color: '#fff',
  },
  colorOptionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  colorOption: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  colorOptionActive: {
    borderColor: '#007AFF',
  },
  quickStyleButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#f0f0f0',
    marginRight: 8,
    marginBottom: 8,
  },
  quickStyleText: {
    textTransform: 'capitalize',
  },
  saveButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 16,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 24,
    marginBottom: 40,
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  backgroundOptions: {
    flex: 1,
    padding: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  backgroundGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  backgroundOption: {
    width: '30%',
    aspectRatio: 1,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
    overflow: 'hidden',
    position: 'relative',
  },
  backgroundOptionActive: {
    borderColor: '#007AFF',
  },
  backgroundName: {
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#007AFF',
    borderStyle: 'dashed',
    marginTop: 8,
  },
  uploadButtonText: {
    marginLeft: 8,
    color: '#007AFF',
    fontWeight: '500',
  },
  customColorSection: {
    marginTop: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  colorInput: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 10,
    fontSize: 16,
  },
  colorPreview: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 10,
  },
  designManagerContent: {
    flex: 1,
    padding: 16,
  },
  designNameInput: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 10,
    fontSize: 16,
    marginBottom: 16,
  },
  actionButtonsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: '#007AFF',
    gap: 8,
  },
  actionButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16,
  },
  noDesignsText: {
    color: '#8e8e93',
    textAlign: 'center',
    marginTop: 20,
  },
  savedDesignItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 10,
    marginBottom: 8,
  },
  savedDesignInfo: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  savedDesignName: {
    fontSize: 16,
    fontWeight: '600',
  },
  savedDesignDate: {
    color: '#8e8e93',
    fontSize: 12,
    marginTop: 4,
  },
  savedDesignActions: {
    flexDirection: 'row',
    gap: 12,
    backgroundColor: 'transparent',
  },
  savedDesignButton: {
    padding: 8,
  },
  layoutOptions: {
    flex: 1,
    padding: 16,
  },
  layoutOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 10,
    marginBottom: 8,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  layoutOptionActive: {
    borderColor: '#007AFF',
  },
  layoutOptionText: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 16,
    flex: 1,
  },
  layoutOptionDesc: {
    fontSize: 12,
    color: '#8e8e93',
    position: 'absolute',
    bottom: 8,
    left: 64,
  },
  pageSizeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 10,
    marginBottom: 8,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  pageSizeOptionActive: {
    borderColor: '#007AFF',
  },
  pageSizePreview: {
    width: 60,
    height: 60,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  pageSizePreviewBox: {
    borderWidth: 2,
    backgroundColor: '#f8f8f8',
  },
  pageSizeInfo: {
    flex: 1,
    marginLeft: 16,
    backgroundColor: 'transparent',
  },
  pageSizeText: {
    fontSize: 18,
    fontWeight: '600',
  },
  pageSizeDesc: {
    fontSize: 14,
    color: '#8e8e93',
    marginTop: 4,
  },
  categoryTabs: {
    flexGrow: 0,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  categoryTab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#007AFF',
    marginRight: 8,
    gap: 6,
  },
  categoryTabActive: {
    backgroundColor: '#007AFF',
  },
  categoryTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#007AFF',
  },
  categoryTabTextActive: {
    color: '#fff',
  },
  patternIndicator: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: 10,
    padding: 2,
  },
  gradientPreview: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 8,
    overflow: 'hidden',
    flexDirection: 'column',
  },
  backgroundCategory: {
    fontSize: 10,
    textTransform: 'capitalize',
    marginTop: 2,
  },
  stylePreviewBox: {
    padding: 16,
    borderRadius: 10,
    marginBottom: 16,
    alignItems: 'center',
  },
  resetButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#FF9500',
    gap: 8,
  },
  resetButtonText: {
    color: '#FF9500',
    fontWeight: '500',
    fontSize: 14,
  },
});
